import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Button, Spinner, Alert, Modal, Dropdown, Tabs, Tab } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import ReactFlow, { Node, Edge, Background, Controls, MiniMap } from 'reactflow'
import 'reactflow/dist/base.css'
import 'reactflow/dist/style.css'
import {
  Activity,
  Eye,
  X,
  Palette,
  Filter,
  Maximize2,
  Minimize2,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Search,
  Settings,
  Database,
} from 'lucide-react'
import { useAppDispatch } from '../../../store/hooks'
import {
  useGeneratePCAMutation,
  contentApi,
  useGetContentMetadataQuery,
} from '../../../services/contentApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { useGetProjectQuery, useVectorizeProjectMutation } from '../../../services/projectsApi'
import { QueryContentResp } from '../../../types/models/content'
import {
  ChartContainer,
  ChartHeader,
  ChartTitle,
  FlowContainer,
  GenerateButton,
  LoadingContainer,
  ContentViewerModal,
  ContentGrid,
  ContentItem,
  ContentImage,
  ContentInfo,
  ButtonOverlay,
  Legend,
  LegendItem,
  LegendItemsContainer,
  LegendColor,
  VectorizationProgress,
  ProgressTitle,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressStats,
  TabsContainer,
  DashboardContainer,
  ChartPanel,
  InsightsPanel,
  HealthScoreContainer,
  StatusBar,
  StatusDot,
  StatusText,
  IssueGroup,
  IssueGroupTitle,
  IssueList,
  IssueItem,
  PairRow,
  PairClasses,
  ClassBadge,
  PairDistance,
  InsightsScrollContainer,
  ActionButton,
} from './styles'
import { DataPointNode, ExpandedDataPointNode, ClusterNode } from './components'

const nodeTypes = {
  dataPoint: DataPointNode,
  cluster: ({ data, id, selected }: any) => <ClusterNode data={data} id={id} selected={selected} />,
  expandedDataPoint: ExpandedDataPointNode,
}

interface DisplacementChartProps {
  isLoading?: boolean
  onStatsUpdate?: (stats: {
    contentData: any
    selectedNodes: Set<string>
    visualizationMode: string
    renderMode: string
    tagIdToNameAndColor: Record<string, { name: string; color: string }>
    onViewContent: () => void
    outlierReasons?: Map<number, string[]>
  }) => void
}

const DisplacementChart: React.FC<DisplacementChartProps> = ({
  isLoading = false,
  onStatsUpdate,
}) => {
  const { projectId } = useParams<{ projectId: string }>()

  // Check if vectorization is in progress to enable polling
  const [shouldPoll, setShouldPoll] = useState(false)

  // Dataset filter state
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const [showDatasetDropdown, setShowDatasetDropdown] = useState(false)

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
    pollingInterval: shouldPoll ? 3000 : undefined, // Poll every 3 seconds when vectorization is in progress
    refetchOnMountOrArgChange: true,
  })

  // Get available datasets
  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])

  // Enable polling if vectorization is in progress and auto-fetch when complete
  React.useEffect(() => {
    if (project?.vectorization) {
      // Poll if status is 1 (Initializing) or 2 (Running)
      const isInProgress =
        project.vectorization.vector_status === 1 || project.vectorization.vector_status === 2
      const wasPolling = shouldPoll
      setShouldPoll(isInProgress)

      // If vectorization just completed successfully (status 3), fetch the content data
      if (wasPolling && !isInProgress && projectId && project.vectorization.vector_status === 3) {
        fetchAllContentPages()
      }
    }
  }, [project?.vectorization, shouldPoll, projectId])

  const [generatePCA, { isLoading: isGeneratingPCA }] = useGeneratePCAMutation()
  const [vectorizeProject, { isLoading: isVectorizing }] = useVectorizeProjectMutation()
  const [showChart, setShowChart] = useState(false)
  const [showMiniMap, setShowMiniMap] = useState(false)
  const [error, setError] = useState('')

  // Visualization mode state
  const [visualizationMode, setVisualizationMode] = useState<
    'default' | 'annotation' | 'source' | 'size' | 'outliers'
  >('annotation')

  // Lasso selection state
  const [lassoMode, setLassoMode] = useState(false)
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set())
  const [lassoPath, setLassoPath] = useState<{ x: number; y: number }[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null)
  const [showContentViewer, setShowContentViewer] = useState(false)

  // Tag visibility state for legend toggle functionality
  const [visibleTags, setVisibleTags] = useState<Set<string>>(new Set())

  // Legend expansion state
  const [expandedLegend, setExpandedLegend] = useState(false)

  // Storage keys for persistence
  const getStorageKey = (projectId: string) => `displacement-chart-${projectId}`
  const getLastProjectKey = () => 'displacement-chart-last-project'

  // Helper function to safely use localStorage with quota handling
  const safeLocalStorage = useMemo(
    () => ({
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value)
        } catch (error) {
          console.warn('localStorage quota exceeded, clearing old displacement chart data')
          // Clear old displacement chart data
          Object.keys(localStorage).forEach((storageKey) => {
            if (storageKey.startsWith('displacement-chart-') && storageKey !== key) {
              localStorage.removeItem(storageKey)
            }
          })
          // Try again after cleanup
          try {
            localStorage.setItem(key, value)
          } catch (retryError) {
            console.error('Failed to store displacement chart data even after cleanup:', retryError)
          }
        }
      },
      getItem: (key: string) => {
        try {
          return localStorage.getItem(key)
        } catch (error) {
          console.warn('Failed to read from localStorage:', error)
          return null
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key)
        } catch (error) {
          console.warn('Failed to remove from localStorage:', error)
        }
      },
    }),
    []
  )

  // Handle project switching - clear old data and load new data
  useEffect(() => {
    if (!projectId) return

    // Check if project has changed
    const lastProject = safeLocalStorage.getItem(getLastProjectKey())
    if (lastProject && lastProject !== projectId) {
      // Clear old project data
      if (lastProject) {
        safeLocalStorage.removeItem(getStorageKey(lastProject))
      }
      // Reset view initialization for new project
      setHasInitializedView(false)
    }

    // Update last project
    safeLocalStorage.setItem(getLastProjectKey(), projectId)

    // Check for persisted data for current project
    const persistedData = safeLocalStorage.getItem(getStorageKey(projectId))
    if (persistedData) {
      try {
        setShowChart(true)
      } catch (error) {
        // Invalid data, remove it
        safeLocalStorage.removeItem(getStorageKey(projectId))
      }
    } else {
      // No persisted data, but try to fetch vectorized content anyway
      // This will auto-show the chart if vectorized data exists
      setShowChart(true)
    }
  }, [projectId])

  // Debug selectedNodes changes
  React.useEffect(() => {
    // Force re-render when selectedNodes changes
  }, [selectedNodes])

  // Performance and rendering state
  const [renderMode, setRenderMode] = useState<'full' | 'sample' | 'viewport' | 'clustered'>(
    'clustered'
  )
  const [sampleSize, setSampleSize] = useState(2000)

  // Clustering parameters - adjusted for better grouping
  const CLUSTER_DISTANCE = 50 // Increased from 30 to cluster more aggressively
  const MIN_CLUSTER_SIZE = 3

  // Cluster expansion state
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
  const [isClusterExpanded, setIsClusterExpanded] = useState(false)

  // Zoom-based dynamic spacing state
  const [currentZoom, setCurrentZoom] = useState(1)

  // Custom pagination hook for fetching all content pages
  // Custom pagination hook for fetching all content pages
  const [allContentData, setAllContentData] = useState<QueryContentResp | null>(null)
  const [isLoadingAllContent, setIsLoadingAllContent] = useState(false)
  const [contentFetchError, setContentFetchError] = useState<string | null>(null)

  const dispatch = useAppDispatch()

  // Function to handle cluster expansion
  const handleClusterClick = useCallback(
    (clusterId: string) => {
      const newExpandedClusters = new Set(expandedClusters)

      if (expandedClusters.has(clusterId)) {
        // Collapse the cluster
        newExpandedClusters.delete(clusterId)
      } else {
        // Expand the cluster
        newExpandedClusters.add(clusterId)
      }

      setExpandedClusters(newExpandedClusters)
      setIsClusterExpanded(newExpandedClusters.size > 0)
    },
    [expandedClusters]
  )

  // Function to reset all cluster expansions
  const resetClusterExpansions = useCallback(() => {
    setExpandedClusters(new Set())
    setIsClusterExpanded(false)
  }, [])

  // Calculate zoom-adjusted positions - spreads nodes out more as zoom increases
  const calculateZoomAdjustedPositions = useCallback((nodes: any[], zoom: number) => {
    if (zoom <= 1.1 || nodes.length === 0) return nodes // No adjustment needed at low zoom levels

    // Calculate the center point of all nodes
    const centerX = nodes.reduce((sum, node) => sum + node.position.x, 0) / nodes.length
    const centerY = nodes.reduce((sum, node) => sum + node.position.y, 0) / nodes.length

    // Much more aggressive spacing multiplier for fine-grained separation
    // At zoom 2x: 2.5x spacing, at zoom 4x: 5x spacing, at zoom 8x: 10x spacing
    const spacingMultiplier = Math.pow(zoom, 1.8)

    return nodes.map((node) => {
      // Calculate distance from center
      const deltaX = node.position.x - centerX
      const deltaY = node.position.y - centerY

      // Apply spacing multiplier with reduced damping for more aggressive separation
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      const dampingFactor = Math.min(1, distance / 10) // Less damping for more separation

      const adjustedMultiplier = 1 + (spacingMultiplier - 1) * dampingFactor

      const newX = centerX + deltaX * adjustedMultiplier
      const newY = centerY + deltaY * adjustedMultiplier

      return {
        ...node,
        position: {
          x: newX,
          y: newY,
        },
      }
    })
  }, [])

  // Function to fetch all pages of content with the working query structure
  const fetchAllContentPages = useCallback(async () => {
    if (!projectId) return

    setIsLoadingAllContent(true)
    setContentFetchError(null)

    try {
      const allContent: any[] = []
      let currentPage = 1
      let hasMorePages = true
      const pageSize = 1000

      // Build base filters
      const baseFilters = [
        {
          key: 'projectid',
          value: projectId,
        },
        {
          key: 'vectorized',
          value: true,
        },
      ]

      // Add datasource filter if datasets are selected
      if (selectedDatasets.length > 0) {
        selectedDatasets.forEach((dataset) => {
          baseFilters.push({
            key: 'metadata.datasource',
            value: dataset,
          })
        })
      }

      // Use 'or' operator if filtering by multiple datasets, 'and' otherwise
      const filterOperator = selectedDatasets.length > 1 ? 'or' : 'and'

      // Test if pagination is 0-based by trying page 0 first
      try {
        const testResponse = await dispatch(
          contentApi.endpoints.queryContent.initiate({
            filters: baseFilters,
            operator: filterOperator as 'and' | 'or',
            limit: pageSize,
            page: 0,
          })
        ).unwrap()

        if (testResponse.content && testResponse.content.length > 0) {
          currentPage = 0
        }
      } catch (error) {
        // Assuming 1-based pagination
      }

      while (hasMorePages) {
        // Use the working query structure that was successful before
        const response = await dispatch(
          contentApi.endpoints.queryContent.initiate({
            filters: baseFilters,
            operator: filterOperator as 'and' | 'or',
            limit: pageSize,
            page: currentPage,
          })
        ).unwrap()

        if (response.content && response.content.length > 0) {
          allContent.push(...response.content)

          // Check if we got a full page - if not, we're done
          // Also check if we've reached the expected total count from the API
          if (response.content.length < pageSize || allContent.length >= response.count) {
            hasMorePages = false
          } else {
            currentPage++
          }
        } else {
          hasMorePages = false
        }

        // Safety check to prevent infinite loops
        if (currentPage > 100) {
          break
        }
      }

      // Auto-adjust render mode based on data size
      // Use clustered view for most datasets, only switch to full for very small datasets
      if (allContent.length < 500) {
        setRenderMode('full') // Use full rendering for small datasets
      } else {
        setRenderMode('clustered') // Use clustering for medium to large datasets (default)
      }

      setAllContentData({
        content: allContent,
        count: allContent.length,
        page: currentPage - 1,
      })

      // If we successfully loaded vectorized content, ensure chart is shown
      if (allContent.length > 0) {
        setShowChart(true)
      }
    } catch (error) {
      console.error('Error fetching all content pages:', error)
      setContentFetchError('Failed to fetch content data')
    } finally {
      setIsLoadingAllContent(false)
    }
  }, [projectId, selectedDatasets, dispatch])

  // Intelligent sampling function that preserves distribution
  const sampleContentData = useCallback((contentArray: any[], targetSize: number) => {
    if (contentArray.length <= targetSize) return contentArray

    // Strategy 1: Stratified sampling by annotation status to preserve distribution
    const annotated = contentArray.filter((item) => item.annotation && item.annotation.length > 0)
    const unannotated = contentArray.filter(
      (item) => !item.annotation || item.annotation.length === 0
    )

    const annotatedRatio = annotated.length / contentArray.length
    const targetAnnotated = Math.floor(targetSize * annotatedRatio)
    const targetUnannotated = targetSize - targetAnnotated

    // Random sampling within each group
    const shuffleArray = (array: any[]) => {
      const shuffled = [...array]
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      return shuffled
    }

    const sampledAnnotated = shuffleArray(annotated).slice(0, targetAnnotated)
    const sampledUnannotated = shuffleArray(unannotated).slice(0, targetUnannotated)

    return [...sampledAnnotated, ...sampledUnannotated]
  }, [])

  // Get processed content data based on render mode
  const processedContentData = useMemo(() => {
    if (!allContentData?.content) return null

    const contentArray = allContentData.content as any[]

    switch (renderMode) {
      case 'full':
        return { ...allContentData, content: contentArray }

      case 'sample':
        const sampledContent = sampleContentData(contentArray, sampleSize)
        return { ...allContentData, content: sampledContent }

      case 'clustered':
        // Return full data for clustering - clustering will handle the performance optimization
        return { ...allContentData, content: contentArray }

      case 'viewport':
        // For now, fall back to sampling. Viewport culling would require more complex implementation
        return {
          ...allContentData,
          content: sampleContentData(contentArray, 1000),
        }

      default:
        return allContentData
    }
  }, [allContentData, renderMode, sampleSize, sampleContentData])

  // Fetch all content when component mounts or project changes
  useEffect(() => {
    if (projectId) {
      // Always try to fetch vectorized content - if it exists, show the chart
      fetchAllContentPages()
    }
  }, [projectId, fetchAllContentPages])

  // Store content data for persistence (with size limiting)
  React.useEffect(() => {
    if (allContentData && projectId && showChart) {
      // Limit stored data size by removing large fields like b64_image
      const compressedData = {
        ...allContentData,
        content: allContentData.content?.map((item: any) => ({
          ...item,
          b64_image: undefined, // Remove large base64 images
          annotation: item.annotation?.map((ann: any) => ({
            ...ann,
            b64_image: undefined, // Remove base64 from annotations too
          })),
        })),
      }
      safeLocalStorage.setItem(getStorageKey(projectId), JSON.stringify(compressedData))
    }
  }, [allContentData, projectId, safeLocalStorage, showChart])

  // Determine which content data to use
  const activeContentData = React.useMemo(() => {
    if (!projectId) return null

    // Use fresh data if available
    return allContentData
  }, [allContentData, projectId])

  // Fetch tags for the project/dataset
  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectId!,
      dataset_id: project?.datasetid || '',
    },
    { skip: !projectId || !project?.datasetid }
  )

  // Create a mapping from tag ID to tag name and color
  const tagIdToNameAndColor = useMemo(() => {
    if (!tagsData?.tags) return {}

    const mapping: Record<string, { name: string; color: string }> = {}
    tagsData.tags.forEach((tag) => {
      // Convert color number to hex color
      const hexColor = `#${tag.color.toString(16).padStart(6, '0')}`
      mapping[tag.id] = {
        name: tag.name,
        color: hexColor,
      }
    })
    return mapping
  }, [tagsData])

  // Helper function to get the most dominant tag from annotations
  const getMostDominantTag = useCallback((annotations: any[] = []) => {
    if (!annotations || annotations.length === 0) return null

    // Count tag occurrences across all annotations
    const tagCounts: Record<string, number> = {}
    annotations.forEach((ann) => {
      if (ann.tagids && Array.isArray(ann.tagids)) {
        ann.tagids.forEach((tagId: string) => {
          tagCounts[tagId] = (tagCounts[tagId] || 0) + 1
        })
      }
    })

    // Find the most frequent tag
    let mostFrequentTag = null
    let maxCount = 0
    Object.entries(tagCounts).forEach(([tagId, count]) => {
      if (count > maxCount) {
        maxCount = count
        mostFrequentTag = tagId
      }
    })

    return mostFrequentTag
  }, [])

  // Initialize visible tags when tag data changes or when switching to annotation mode
  useEffect(() => {
    if (visualizationMode === 'annotation') {
      // Show all tags by default
      if (tagsData?.tags) {
        const allTagIds = new Set(tagsData.tags.map((tag) => tag.id))
        allTagIds.add('no-tags')
        setVisibleTags(allTagIds)
      } else {
        setVisibleTags(new Set(['no-tags']))
      }
    } else {
      // Clear visible tags when not in annotation mode
      setVisibleTags(new Set())
    }
  }, [tagsData, visualizationMode])

  // Helper function to get tag names from tag IDs
  const getTagNames = useCallback(
    (tagIds: string[] = []) => {
      return tagIds.map((id) => tagIdToNameAndColor[id]?.name || id).join(', ')
    },
    [tagIdToNameAndColor]
  )

  // Helper function to render tag badges with colors
  const renderTagBadges = useCallback(
    (tagIds: string[] = []) => {
      if (!tagIds || tagIds.length === 0) return null

      return tagIds
        .map((id) => {
          const tagInfo = tagIdToNameAndColor[id]
          if (!tagInfo) return null

          return (
            <span
              key={id}
              style={{
                display: 'inline-block',
                backgroundColor: tagInfo.color,
                color: '#ffffff',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                marginRight: '0.25rem',
                marginBottom: '0.25rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              {tagInfo.name}
            </span>
          )
        })
        .filter(Boolean)
    },
    [tagIdToNameAndColor]
  )

  // Helper function to get all tags from an item's annotations
  const getAllTagsFromItem = useCallback((item: any): string[] => {
    if (!item.annotation || !Array.isArray(item.annotation)) return []

    const allTags: string[] = []
    item.annotation.forEach((ann: any) => {
      if (ann.tagids && Array.isArray(ann.tagids)) {
        allTags.push(...ann.tagids)
      }
    })
    return [...new Set(allTags)] // Remove duplicates
  }, [])

  // Helper function to check if an item should be visible based on tag visibility settings
  const isItemVisibleByTag = useCallback(
    (item: any, index?: number): boolean => {
      if (visualizationMode !== 'annotation') {
        return true
      }

      if (visibleTags.size === 0) {
        return true // Show all when no visibility rules are set
      }

      // Check if item has annotations
      if (!item.annotation || item.annotation.length === 0) {
        // No annotations - check if "no-tags" is visible
        const visible = visibleTags.has('no-tags')
        return visible
      }

      // Item has annotations - check if its dominant tag is visible
      const dominantTagId = getMostDominantTag(item.annotation)

      if (dominantTagId) {
        // Item has a dominant tag - show only if that tag is visible
        const visible = visibleTags.has(dominantTagId)
        return visible
      }

      // If no dominant tag found, fall back to checking "no-tags" visibility
      // (for items with annotations but no recognizable tags)
      const fallbackVisible = visibleTags.has('no-tags')
      return fallbackVisible
    },
    [visualizationMode, visibleTags, getMostDominantTag]
  )

  // Helper function to get unique sources from the dataset
  const getUniqueSources = useMemo(() => {
    if (!activeContentData?.content) return []

    const sources = new Set<string>()
    activeContentData.content.forEach((item: any) => {
      if (item.metadata?.datasource) {
        sources.add(item.metadata.datasource)
      }
    })
    return Array.from(sources).sort()
  }, [activeContentData])

  // Outlier detection functions with detailed reasons
  const { detectOutliers, outlierReasons } = useMemo(() => {
    if (!activeContentData?.content)
      return {
        detectOutliers: new Set<number>(),
        outlierReasons: new Map<number, string[]>(),
      }

    const contentArray = activeContentData.content as any[]
    const outliers = new Set<number>()
    const reasons = new Map<number, string[]>()

    // Helper function to add reason
    const addReason = (index: number, reason: string) => {
      if (!reasons.has(index)) {
        reasons.set(index, [])
      }
      reasons.get(index)?.push(reason)
      outliers.add(index)
    }

    // Method 1: Vector distance outliers (isolation in PCA space)
    const vectors = contentArray
      .map((item: any, index: number) => ({ vector: item.vector, index }))
      .filter((item) => item.vector && item.vector.length >= 2)

    if (vectors.length > 10) {
      // Calculate centroid
      const centroidX = vectors.reduce((sum, item) => sum + item.vector[0], 0) / vectors.length
      const centroidY = vectors.reduce((sum, item) => sum + item.vector[1], 0) / vectors.length

      // Calculate distances from centroid
      const distances = vectors.map((item) => ({
        index: item.index,
        distance: Math.sqrt(
          Math.pow(item.vector[0] - centroidX, 2) + Math.pow(item.vector[1] - centroidY, 2)
        ),
      }))

      // Find outliers using IQR method
      const sortedDistances = distances.map((d) => d.distance).sort((a, b) => a - b)
      const q1 = sortedDistances[Math.floor(sortedDistances.length * 0.25)]
      const q3 = sortedDistances[Math.floor(sortedDistances.length * 0.75)]
      const iqr = q3 - q1
      const outlierThreshold = q3 + 1.5 * iqr

      distances.forEach((item) => {
        if (item.distance > outlierThreshold) {
          const percentile = (
            (sortedDistances.filter((d) => d < item.distance).length / sortedDistances.length) *
            100
          ).toFixed(1)
          addReason(
            item.index,
            `Isolated in PCA space (${percentile}th percentile distance from center)`
          )
        }
      })
    }

    // Method 2: Size outliers (unusual image dimensions)
    const imageSizes = contentArray
      .map((item: any, index: number) => ({
        index,
        width: item.width,
        height: item.height,
        area: item.width && item.height ? item.width * item.height : null,
      }))
      .filter((item) => item.area !== null)

    if (imageSizes.length > 10) {
      const areas = imageSizes.map((item) => item?.area || 0).sort((a, b) => a - b)
      const q1 = areas[Math.floor(areas.length * 0.25)]
      const q3 = areas[Math.floor(areas.length * 0.75)]
      const iqr = q3 - q1
      const lowerBound = q1 - 1.5 * iqr
      const upperBound = q3 + 1.5 * iqr
      const median = areas[Math.floor(areas.length * 0.5)]

      imageSizes.forEach((item) => {
        if ((item.area ?? 0) < lowerBound) {
          const megapixels = ((item.area ?? 0) / 1000000).toFixed(2)
          const medianMp = (median / 1000000).toFixed(2)
          addReason(
            item.index,
            `Unusually small image (${item.width}×${item.height}, ${megapixels}MP vs median ${medianMp}MP)`
          )
        } else if ((item.area ?? 0) > upperBound) {
          const megapixels = ((item.area ?? 0) / 1000000).toFixed(2)
          const medianMp = (median / 1000000).toFixed(2)
          addReason(
            item.index,
            `Unusually large image (${item.width}×${item.height}, ${megapixels}MP vs median ${medianMp}MP)`
          )
        }
      })
    }

    // Method 3: Annotation density outliers
    const annotationCounts = contentArray.map((item: any, index: number) => ({
      index,
      count: item.annotation ? item.annotation.length : 0,
    }))

    if (annotationCounts.length > 10) {
      const counts = annotationCounts.map((item) => item.count).sort((a, b) => a - b)
      const q3 = counts[Math.floor(counts.length * 0.75)]
      const median = counts[Math.floor(counts.length * 0.5)]
      const upperBound = q3 + 1.5 * (q3 - counts[Math.floor(counts.length * 0.25)])

      // Only flag items with unusually high annotation counts as outliers
      annotationCounts.forEach((item) => {
        if (item.count > upperBound && item.count > 5) {
          // At least 5 annotations to be considered
          addReason(
            item.index,
            `High annotation density (${item.count} annotations vs median ${median})`
          )
        }
      })
    }

    // Method 4: Tag diversity outliers (items with unusually many different tags)
    const tagDiversities = contentArray.map((item: any, index: number) => {
      const uniqueTags = new Set<string>()
      if (item.annotation && Array.isArray(item.annotation)) {
        item.annotation.forEach((ann: any) => {
          if (ann.tagids && Array.isArray(ann.tagids)) {
            ann.tagids.forEach((tagId: string) => uniqueTags.add(tagId))
          }
        })
      }
      return { index, diversity: uniqueTags.size }
    })

    if (tagDiversities.length > 10) {
      const diversities = tagDiversities.map((item) => item.diversity).sort((a, b) => a - b)
      const q3 = diversities[Math.floor(diversities.length * 0.75)]
      const median = diversities[Math.floor(diversities.length * 0.5)]

      // Flag items with significantly more tag diversity than median
      tagDiversities.forEach((item) => {
        if (item.diversity > q3 && item.diversity > median * 2 && item.diversity >= 3) {
          addReason(
            item.index,
            `High tag diversity (${item.diversity} unique tags vs median ${median})`
          )
        }
      })
    }

    return { detectOutliers: outliers, outlierReasons: reasons }
  }, [activeContentData])

  // Helper function to get source color
  const getSourceColor = useCallback(
    (source: string) => {
      const sourceColors = [
        '#114ee0',
        '#28a745',
        '#dc3545',
        '#ffc107',
        '#17a2b8',
        '#6f42c1',
        '#fd7e14',
        '#20c997',
      ]
      const sourceIndex = getUniqueSources.indexOf(source)
      return sourceIndex >= 0 ? sourceColors[sourceIndex % sourceColors.length] : '#6c757d'
    },
    [getUniqueSources]
  )

  // Helper function to get node color based on visualization mode
  const getNodeColor = useCallback(
    (item: any, index: number) => {
      switch (visualizationMode) {
        case 'annotation':
          if (item.annotation && item.annotation.length > 0) {
            const dominantTagId = getMostDominantTag(item.annotation)
            if (dominantTagId && tagIdToNameAndColor[dominantTagId]) {
              return tagIdToNameAndColor[dominantTagId].color
            }
            return '#28a745' // Green for annotated but no tag info
          }
          return '#6c757d' // Gray for unannotated

        case 'source':
          // Color by actual data source from metadata
          if (item.metadata?.datasource) {
            return getSourceColor(item.metadata.datasource)
          }
          return '#6c757d' // Gray for unknown/missing source

        case 'size':
          // Color by image dimensions (max dimension)
          if (item.width && item.height) {
            const maxDimension = Math.max(item.width, item.height)
            if (maxDimension >= 2048) return '#e83e8c' // Pink for huge (2K+)
            if (maxDimension >= 1024) return '#6f42c1' // Purple for very large (1K-2K)
            if (maxDimension >= 512) return '#17a2b8' // Teal for large (512-1K)
            if (maxDimension >= 256) return '#007bff' // Blue for medium (256-512)
            if (maxDimension >= 128) return '#20c997' // Mint green for small (128-256)
            return '#fd7e14' // Orange for tiny (<128)
          }
          return '#6c757d' // Gray for unknown size

        case 'outliers':
          // Color by outlier status
          if (detectOutliers.has(index)) {
            return '#dc3545' // Red for outliers
          }
          return '#28a745' // Green for normal items

        case 'default':
        default:
          // Original logic: green for annotated, blue for unannotated
          return item.annotation && item.annotation.length > 0 ? '#28a745' : '#114ee0'
      }
    },
    [visualizationMode, getMostDominantTag, tagIdToNameAndColor, getSourceColor, detectOutliers]
  )

  // Helper function to get node label based on visualization mode
  const getNodeLabel = useCallback(
    (item: any, index: number) => {
      const baseName = item.name || `Point ${index + 1}`

      switch (visualizationMode) {
        case 'annotation':
          if (item.annotation && item.annotation.length > 0) {
            const dominantTagId = getMostDominantTag(item.annotation)
            const tagInfo = dominantTagId ? tagIdToNameAndColor[dominantTagId] : null
            return tagInfo ? `${baseName}\n(${tagInfo.name})` : baseName
          }
          return `${baseName}\n(No annotations)`

        case 'source':
          return item.metadata?.datasource ? `${baseName}\n(${item.metadata.datasource})` : baseName

        case 'size':
          if (item.width && item.height) {
            const maxDimension = Math.max(item.width, item.height)
            let sizeCategory = ''
            if (maxDimension >= 2048) sizeCategory = 'Huge'
            else if (maxDimension >= 1024) sizeCategory = 'Very Large'
            else if (maxDimension >= 512) sizeCategory = 'Large'
            else if (maxDimension >= 256) sizeCategory = 'Medium'
            else if (maxDimension >= 128) sizeCategory = 'Small'
            else sizeCategory = 'Tiny'
            return `${baseName}\n(${item.width}×${item.height} - ${sizeCategory})`
          }
          return baseName

        case 'outliers':
          if (detectOutliers.has(index)) {
            const itemReasons = outlierReasons.get(index) || ['Statistical outlier']
            const reasonText = itemReasons.join('\n• ')
            return `${baseName}\n\n🚨 OUTLIER DETECTED:\n• ${reasonText}`
          }
          return `${baseName}\n✅ Normal item`

        case 'default':
        default:
          return baseName
      }
    },
    [
      visualizationMode,
      getMostDominantTag,
      tagIdToNameAndColor,
      detectOutliers,
      outlierReasons,
      activeContentData,
    ]
  )

  // Clustering algorithm - groups nearby nodes of the same color
  const clusterNodes = useCallback(
    (items: any[]) => {
      if (!items || items.length === 0) return { clusters: [], individualNodes: [] }

      // Always cluster the full dataset to maintain PCA accuracy and stable positions

      // First, create all individual nodes with positions
      const allNodes = items.map((item: any, index: number) => ({
        id: `node-${item.originalIndex !== undefined ? item.originalIndex : index}`,
        item,
        index: item.originalIndex !== undefined ? item.originalIndex : index,
        color: getNodeColor(item, item.originalIndex !== undefined ? item.originalIndex : index),
        position: null as { x: number; y: number } | null,
        clustered: false,
      }))

      // Calculate positions for all nodes
      const vectors = items.map((item: any) => item.vector)
      const xValues = vectors.map((v: number[]) => v[0])
      const yValues = vectors.map((v: number[]) => v[1])
      const xMin = Math.min(...xValues)
      const xMax = Math.max(...xValues)
      const yMin = Math.min(...yValues)
      const yMax = Math.max(...yValues)

      // Use the same dimensions as the main chart for consistency
      const CHART_WIDTH = 1200
      const CHART_HEIGHT = 800
      const CHART_MARGIN = 100

      allNodes.forEach((node) => {
        const normalizedX = (node.item.vector[0] - xMin) / (xMax - xMin || 1)
        const normalizedY = (node.item.vector[1] - yMin) / (yMax - yMin || 1)
        node.position = {
          x: normalizedX * CHART_WIDTH + CHART_MARGIN,
          y: normalizedY * CHART_HEIGHT + CHART_MARGIN,
        }
        // Create a copy of the item with calculated positions instead of modifying the original
        node.item = {
          ...node.item,
          calculatedX: node.position.x,
          calculatedY: node.position.y,
        }
      })

      // Group nodes - by tag in annotation mode, by color otherwise
      const groups: Record<string, typeof allNodes> = {}
      allNodes.forEach((node) => {
        let groupKey = node.color

        // In annotation mode, group by actual tag ID to ensure same-class clustering
        if (visualizationMode === 'annotation') {
          const dominantTagId = getMostDominantTag(node.item.annotation)
          groupKey = dominantTagId || 'no-tag'
        }

        if (!groups[groupKey]) {
          groups[groupKey] = []
        }
        groups[groupKey].push(node)
      })

      const clusters: any[] = []
      const individualNodes: any[] = []

      // Process each group separately
      Object.entries(groups).forEach(([groupKey, nodes], index: number) => {
        const color = nodes[0]?.color || '#6c757d'
        const unclustered = [...nodes]

        while (unclustered.length > 0) {
          const seed = unclustered.shift() || null
          const cluster = [seed]

          // Find nearby nodes of the same color
          for (let i = unclustered.length - 1; i >= 0; i--) {
            const candidate = unclustered[i]
            if (!seed || !candidate) continue

            const dx = (seed.position?.x || 0) - (candidate.position?.x || 0)
            const dy = (seed.position?.y || 0) - (candidate.position?.y || 0)
            const distance = Math.sqrt(dx * dx + dy * dy)

            if (distance <= CLUSTER_DISTANCE) {
              cluster.push(candidate)
              unclustered.splice(i, 1)
            }
          }

          // Create cluster or individual node
          if (cluster.length >= MIN_CLUSTER_SIZE) {
            // Calculate cluster center
            const centerX =
              cluster.reduce((sum, node) => sum + (node?.position?.x || 0), 0) / cluster.length
            const centerY =
              cluster.reduce((sum, node) => sum + (node?.position?.y || 0), 0) / cluster.length

            const clusterId = `cluster-${clusters.length}`
            clusters.push({
              id: clusterId,
              type: 'cluster',
              position: { x: centerX, y: centerY },
              data: {
                color,
                count: cluster.length,
                items: cluster.map((n: any) => n?.item),
                nodeIds: cluster.map((n: any) => n?.id || `node-${index}`),
              },
              draggable: false,
              selectable: true,
              selected: selectedNodes.has(clusterId),
            })
          } else {
            // Add as individual nodes
            cluster.forEach((node: any, index: number) => {
              individualNodes.push({
                id: node?.id || `node-${index}`,
                type: 'dataPoint',
                position: node?.position || { x: 0, y: 0 },
                data: {
                  label: getNodeLabel(node?.item, node?.index || index),
                  color: node?.color,
                  content: node?.item,
                  selected: selectedNodes.has(node?.id || `node-${index}`),
                },
                draggable: false,
                selectable: true,
                selected: selectedNodes.has(node.id),
              })
            })
          }
        }
      })

      return { clusters, individualNodes }
    },
    [getNodeColor, getNodeLabel, selectedNodes, visualizationMode, getMostDominantTag]
  )

  // Function to expand a cluster - instead of moving nodes, we'll highlight them in place
  const expandClusterNodes = useCallback(
    (cluster: any) => {
      const { items, nodeIds } = cluster.data

      // Create individual nodes at their actual PCA positions with visual enhancements
      return items.map((item: any, index: number) => {
        // We need to recalculate the actual position for each item using the global scaling
        // This requires access to all filtered items to get the proper min/max bounds
        const nodeId = nodeIds[index] || `expanded-${cluster.id}-${index}`
        const isSelected = selectedNodes.has(nodeId)

        return {
          id: nodeId,
          type: 'expandedDataPoint', // Use a special type for expanded nodes
          position: {
            x: item.calculatedX || cluster.position.x, // Fallback to cluster center if no calculated position
            y: item.calculatedY || cluster.position.y,
          },
          data: {
            label: getNodeLabel(item, index),
            color: cluster.data.color,
            content: item,
            selected: isSelected,
            isExpanded: true,
            parentClusterId: cluster.id,
            clusterCenter: cluster.position,
            // Enhanced visual properties
            glowColor: cluster.data.color,
            showConnection: true,
          },
          draggable: false,
          selectable: true,
          selected: isSelected,
        }
      })
    },
    [selectedNodes, getNodeLabel]
  )

  const handleGeneratePCA = async () => {
    if (!projectId) return

    try {
      setError('')

      // Clear existing data when regenerating PCA
      if (activeContentData) {
        safeLocalStorage.removeItem(getStorageKey(projectId))
      }

      // Reset view initialization for new PCA
      setHasInitializedView(false)

      await generatePCA({ projectId }).unwrap()
      setShowChart(true)

      // Refetch content data after PCA generation
      setTimeout(() => {
        fetchAllContentPages()
      }, 1500) // Give the backend time to process
    } catch (err: any) {
      // More detailed error handling
      let errorMessage = 'Failed to generate PCA'

      if (err?.status === 400) {
        errorMessage = 'PCA generation failed - please check your data has valid vectors'
      } else if (err?.status === 409) {
        errorMessage = 'PCA already exists and is up to date'
      } else if (err?.status === 500) {
        errorMessage = 'Server error during PCA generation - please try again'
      } else if (err?.data?.message) {
        errorMessage = err.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)

      // If it's a "conflict" error (PCA already exists), still show the chart
      if (err?.status === 409 && activeContentData) {
        setShowChart(true)
      }
    }
  }

  const handleVectorizeProject = async () => {
    if (!projectId) return

    try {
      setError('')

      await vectorizeProject({ projectId }).unwrap()

      // Show success message or handle completion
      console.log('Project vectorization completed successfully')

      // Optionally refetch content data after vectorization
      setTimeout(() => {
        fetchAllContentPages()
      }, 1000) // Give the backend time to process
    } catch (err: any) {
      // Error handling
      let errorMessage = 'Failed to vectorize project'

      if (err?.status === 400) {
        errorMessage = 'Vectorization failed - please check your project data'
      } else if (err?.status === 409) {
        errorMessage = 'Project is already vectorized'
      } else if (err?.status === 500) {
        errorMessage = 'Server error during vectorization - please try again'
      } else if (err?.data?.message) {
        errorMessage = err.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    }
  }

  // Convert content data to nodes for ReactFlow (now using processed data)
  const { nodes, edges } = useMemo(() => {
    if (!processedContentData?.content) {
      return { nodes: [], edges: [] }
    }

    // Access the content array directly
    const contentArray = processedContentData.content as any[]

    // Filter items with vectors, but preserve original indices
    const filteredItems = contentArray
      .map((item: any, originalIndex: number) => ({ ...item, originalIndex }))
      .filter((item: any) => item.vector && item.vector.length >= 2)

    // Use clustering if in clustered mode
    if (renderMode === 'clustered') {
      const { clusters, individualNodes } = clusterNodes(filteredItems)

      // Handle cluster expansion - replace expanded clusters with their individual nodes
      const finalNodes: any[] = []

      clusters.forEach((cluster) => {
        if (expandedClusters.has(cluster.id)) {
          // Cluster is expanded - add individual nodes arranged around cluster center
          const expandedNodes = expandClusterNodes(cluster)
          finalNodes.push(...expandedNodes)

          // Add the cluster node but make it transparent/hidden when expanded
          finalNodes.push({
            ...cluster,
            data: {
              ...cluster.data,
              isExpanded: true,
              isHidden: true, // Mark as hidden so it doesn't interfere with expanded nodes
            },
            selected: selectedNodes.has(cluster.id),
          })
        } else {
          // Cluster is collapsed - add the cluster node
          finalNodes.push({
            ...cluster,
            selected: selectedNodes.has(cluster.id),
          })
        }
      })

      // Add individual nodes (non-clustered items)
      finalNodes.push(...individualNodes)

      return { nodes: finalNodes, edges: [] }
    }

    // Original non-clustered logic
    // Always use full dataset for accurate PCA positioning

    // Calculate bounds for proper scaling using full dataset
    const vectors = filteredItems.map((item: any) => item.vector)
    const xValues = vectors.map((v: number[]) => v[0])
    const yValues = vectors.map((v: number[]) => v[1])
    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)
    const yMin = Math.min(...yValues)
    const yMax = Math.max(...yValues)

    // Use a large chart area to better utilize the available ReactFlow space
    const CHART_WIDTH = 1200
    const CHART_HEIGHT = 800
    const CHART_MARGIN = 100

    const flowNodes: Node[] = filteredItems.map((item: any, index: number) => {
      // Normalize vectors to 0-1 range, then scale to chart dimensions
      const normalizedX = (item.vector[0] - xMin) / (xMax - xMin || 1)
      const normalizedY = (item.vector[1] - yMin) / (yMax - yMin || 1)

      const originalIndex = item.originalIndex !== undefined ? item.originalIndex : index
      const nodeId = `node-${originalIndex}`
      const isSelected = selectedNodes.has(nodeId)

      const node = {
        id: nodeId,
        type: 'dataPoint',
        position: {
          x: normalizedX * CHART_WIDTH + CHART_MARGIN,
          y: normalizedY * CHART_HEIGHT + CHART_MARGIN,
        },
        data: {
          label: getNodeLabel(item, originalIndex),
          color: getNodeColor(item, originalIndex),
          content: item,
          selected: isSelected,
        },
        draggable: false,
        selectable: true,
        selected: isSelected,
      }

      return node
    })

    return { nodes: flowNodes, edges: [] }
  }, [
    processedContentData,
    selectedNodes,
    getNodeLabel,
    getNodeColor,
    renderMode,
    clusterNodes,
    expandedClusters,
    expandClusterNodes,
  ])

  // Filter nodes for rendering while maintaining accurate positions
  const filteredNodesForRendering = useMemo(() => {
    if (visualizationMode !== 'annotation' || visibleTags.size === 0) {
      // No filtering needed - show all nodes
      return nodes
    }

    // Filter nodes based on tag visibility
    const filtered = nodes.filter((node) => {
      if (node.type === 'cluster') {
        // For clusters, check if any items in the cluster should be visible
        const clusterData = node.data
        if (clusterData.items && Array.isArray(clusterData.items)) {
          return clusterData.items.some((item: any) => isItemVisibleByTag(item))
        }
        return true
      } else if (node.type === 'expandedDataPoint') {
        // For expanded nodes, check the content
        return isItemVisibleByTag(node.data.content)
      } else {
        // For regular nodes, check the content
        return isItemVisibleByTag(node.data.content)
      }
    })

    return filtered
  }, [nodes, visualizationMode, visibleTags, isItemVisibleByTag])

  // Apply zoom-based spacing to improve separation when zoomed in
  const zoomAdjustedNodes = useMemo(() => {
    return calculateZoomAdjustedPositions(filteredNodesForRendering, currentZoom)
  }, [filteredNodesForRendering, currentZoom, calculateZoomAdjustedPositions])

  const [nodesState, setNodesState] = useState<Node[]>(zoomAdjustedNodes)
  const [edgesState, setEdgesState] = useState<Edge[]>(edges)

  // Get selected content items - filter by current tag visibility
  // This needs to handle clusters properly by counting all items in a cluster
  const selectedContentItems = useMemo(() => {
    if (selectedNodes.size === 0) return []

    // Use allContentData for accurate count, not processedContentData
    if (!allContentData?.content) return []

    // If all (or nearly all) nodes are selected, just return all content
    // This handles the case where lasso selects everything
    const totalNodes = nodesState.length
    const selectedNodeCount = selectedNodes.size
    const selectionRatio = totalNodes > 0 ? selectedNodeCount / totalNodes : 0

    // If more than 90% of nodes are selected, assume "select all" and return all items
    if (selectionRatio > 0.9) {
      return allContentData.content.filter((item: any) => isItemVisibleByTag(item))
    }

    const selectedItems: any[] = []
    const addedItemIds = new Set<string>() // Track to avoid duplicates

    // Find all nodes (including clusters) to get their data
    const allNodes = nodesState

    selectedNodes.forEach((nodeId) => {
      const node = allNodes.find((n) => n.id === nodeId)

      if (!node) {
        // Fallback: try to get from content array by index
        const nodeIndex = parseInt(nodeId.replace('node-', '').replace('cluster-', ''))
        if (!isNaN(nodeIndex) && allContentData.content[nodeIndex]) {
          const contentItem = allContentData.content[nodeIndex]
          if (
            contentItem?.id &&
            !addedItemIds.has(contentItem.id) &&
            isItemVisibleByTag(contentItem)
          ) {
            selectedItems.push(contentItem)
            addedItemIds.add(contentItem.id)
          }
        }
        return
      }

      if (node.type === 'cluster' && node.data?.items) {
        // For clusters, add all items in the cluster
        node.data.items.forEach((item: any) => {
          if (item?.id && !addedItemIds.has(item.id) && isItemVisibleByTag(item)) {
            selectedItems.push(item)
            addedItemIds.add(item.id)
          }
        })
      } else if (node.type === 'dataPoint' || node.type === 'expandedDataPoint') {
        // For regular nodes, find the item from allContentData
        const nodeIndex = parseInt(nodeId.replace('node-', ''))
        if (!isNaN(nodeIndex) && allContentData.content[nodeIndex]) {
          const contentItem = allContentData.content[nodeIndex]
          if (
            contentItem?.id &&
            !addedItemIds.has(contentItem.id) &&
            isItemVisibleByTag(contentItem)
          ) {
            selectedItems.push(contentItem)
            addedItemIds.add(contentItem.id)
          }
        }
      }
    })

    return selectedItems
  }, [allContentData, selectedNodes, isItemVisibleByTag, nodesState])

  // Update nodes when content data OR selection changes (but not while modal is open or drawing)
  React.useEffect(() => {
    if (!showContentViewer && !isDrawing) {
      const hasSelection = selectedNodes.size > 0

      // Apply selection state to nodes
      const nodesWithSelection = zoomAdjustedNodes.map((node) => {
        // For clusters, check if any of its nodeIds are selected
        if (node.type === 'cluster' && node.data?.nodeIds) {
          const hasSelectedChild = node.data.nodeIds.some((nodeId: string) =>
            selectedNodes.has(nodeId)
          )
          return {
            ...node,
            selected: hasSelectedChild,
            data: {
              ...node.data,
              selected: hasSelectedChild,
              hasSelection,
            },
          }
        }

        // For regular nodes, check direct selection
        return {
          ...node,
          selected: selectedNodes.has(node.id),
          data: {
            ...node.data,
            selected: selectedNodes.has(node.id),
            hasSelection,
          },
        }
      })

      const selectedCount = nodesWithSelection.filter((n) => n.selected || n.data?.selected).length
      console.log(
        'Updating nodes. Total:',
        nodesWithSelection.length,
        'Selected IDs:',
        selectedNodes.size,
        'Selected nodes:',
        selectedCount
      )
      setNodesState(nodesWithSelection)
    }
  }, [zoomAdjustedNodes, showContentViewer, isDrawing, selectedNodes])

  // Update edges when content data changes (but not while modal is open or drawing)
  React.useEffect(() => {
    if (!showContentViewer && !isDrawing) {
      setEdgesState(edges)
    }
  }, [edges, showContentViewer, isDrawing])

  const onNodesChange = useCallback((changes: any) => {
    // Handle node changes if needed
  }, [])

  const onEdgesChange = useCallback((changes: any) => {
    // Handle edge changes if needed
  }, [])

  // Point-in-polygon algorithm for lasso selection
  const isPointInPolygon = useCallback(
    (point: { x: number; y: number }, polygon: { x: number; y: number }[]) => {
      if (polygon.length < 3) return false

      let inside = false
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        if (
          polygon[i].y > point.y !== polygon[j].y > point.y &&
          point.x <
            ((polygon[j].x - polygon[i].x) * (point.y - polygon[i].y)) /
              (polygon[j].y - polygon[i].y) +
              polygon[i].x
        ) {
          inside = !inside
        }
      }
      return inside
    },
    []
  )

  // Optimized lasso selection handler
  const handleLassoComplete = useCallback(() => {
    if (lassoPath.length < 3 || !reactFlowInstance) {
      setLassoPath([])
      setIsDrawing(false)
      return
    }

    // Batch all state updates together
    const newSelectedNodes = new Set<string>()

    // Transform lasso path from screen to ReactFlow coordinates
    const transformedLassoPath = lassoPath.map((point) =>
      reactFlowInstance.screenToFlowPosition({ x: point.x, y: point.y })
    )

    // Process selection in a single pass - check each node against lasso polygon
    nodesState.forEach((node) => {
      // Skip hidden nodes based on current filter
      if (node.hidden) return

      const nodeCenter = { x: node.position.x + 4, y: node.position.y + 4 }
      if (isPointInPolygon(nodeCenter, transformedLassoPath)) {
        if (node.type === 'cluster') {
          // For clusters, select all individual nodes within the cluster
          const clusterData = node.data
          if (clusterData.nodeIds && Array.isArray(clusterData.nodeIds)) {
            clusterData.nodeIds.forEach((nodeId: string) => {
              newSelectedNodes.add(nodeId)
            })
            // Don't add the cluster node itself - it's not an image
          }
        } else {
          // For individual nodes, just add them directly
          newSelectedNodes.add(node.id)
        }
      }
    })

    // Batch all state updates
    console.log(
      'Lasso complete. Selected nodes:',
      newSelectedNodes.size,
      Array.from(newSelectedNodes)
    )
    setSelectedNodes(newSelectedNodes)
    setLassoPath([])
    setIsDrawing(false)

    // Immediately update node states to show selection
    const hasSelection = newSelectedNodes.size > 0
    setNodesState((currentNodes) =>
      currentNodes.map((node) => {
        // For clusters, check if any of its nodeIds are selected
        if (node.type === 'cluster' && node.data?.nodeIds) {
          const hasSelectedChild = node.data.nodeIds.some((nodeId: string) =>
            newSelectedNodes.has(nodeId)
          )
          return {
            ...node,
            selected: hasSelectedChild,
            data: {
              ...node.data,
              selected: hasSelectedChild,
              hasSelection,
            },
          }
        }

        // For regular nodes, check direct selection
        return {
          ...node,
          selected: newSelectedNodes.has(node.id),
          data: {
            ...node.data,
            selected: newSelectedNodes.has(node.id),
            hasSelection,
          },
        }
      })
    )
  }, [lassoPath, nodesState, isPointInPolygon, reactFlowInstance])

  // Optimized mouse event handlers for lasso drawing
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || event.button !== 0) return // Only left click

      event.preventDefault()
      event.stopPropagation()

      const x = event.clientX
      const y = event.clientY

      setIsDrawing(true)
      setLassoPath([{ x, y }])
      setSelectedNodes(new Set()) // Clear previous selection
    },
    [lassoMode]
  )

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || !isDrawing) return

      event.preventDefault()
      event.stopPropagation()

      const x = event.clientX
      const y = event.clientY

      // Throttle mouse move updates to improve performance
      setLassoPath((prev) => {
        // Only add point if it's significantly different from the last point
        const lastPoint = prev[prev.length - 1]
        if (lastPoint && Math.abs(lastPoint.x - x) < 3 && Math.abs(lastPoint.y - y) < 3) {
          return prev // Skip this update if the movement is too small
        }
        return [...prev, { x, y }]
      })
    },
    [lassoMode, isDrawing]
  )

  const handleMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (!lassoMode || !isDrawing) return

      event.preventDefault()
      event.stopPropagation()

      handleLassoComplete()
    },
    [lassoMode, isDrawing, handleLassoComplete]
  )

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (lassoMode) {
        event.preventDefault() // Prevent context menu in lasso mode
      }
    },
    [lassoMode]
  )

  const isLoading_ = isLoading || isGeneratingPCA || isVectorizing || isLoadingAllContent

  // Helper function to render legend based on visualization mode
  const renderLegend = useCallback(() => {
    switch (visualizationMode) {
      case 'default':
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Default</div>
            <LegendItem>
              <LegendColor color="#28a745" />
              <span>Annotated</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#114ee0" />
              <span>Unannotated</span>
            </LegendItem>
          </Legend>
        )

      case 'annotation':
        const uniqueTags = Array.from(
          new Set(
            (processedContentData?.content || [])
              .filter((item: any) => item.annotation && item.annotation.length > 0)
              .map((item: any) => getMostDominantTag(item.annotation))
              .filter(Boolean)
          )
        )

        const handleTagToggle = (tagId: string) => {
          setVisibleTags((currentVisibleTags) => {
            // If this is the first time using the legend (all tags visible by default)
            if (currentVisibleTags.size === 0) {
              // Initialize with all available tags, then remove the clicked one
              const allTags = new Set<string>()
              uniqueTags.forEach((tag) => {
                if (tag) allTags.add(tag as string)
              })
              allTags.add('no-tags')
              allTags.delete(tagId) // Remove the clicked tag to "hide" it
              return allTags
            }

            // Normal toggle behavior using current state
            const newVisibleTags = new Set(currentVisibleTags)
            if (newVisibleTags.has(tagId)) {
              newVisibleTags.delete(tagId)
            } else {
              newVisibleTags.add(tagId)
            }
            return newVisibleTags
          })
        }

        return (
          <Legend $expanded={expandedLegend}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                By Tag
                <div
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 'normal',
                    opacity: 0.8,
                    marginTop: '0.2rem',
                  }}
                >
                  Click to toggle visibility
                </div>
              </div>
              <button
                onClick={() => setExpandedLegend(!expandedLegend)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '0.25rem',
                  color: '#ffffff',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)'
                }}
                title={expandedLegend ? 'Collapse to list view' : 'Expand to grid view'}
              >
                {expandedLegend ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
              </button>
            </div>
            <LegendItemsContainer $expanded={expandedLegend}>
              {uniqueTags
                .map((tagId) => {
                  if (!tagId) return null
                  const tagInfo = tagIdToNameAndColor[tagId as string]
                  if (!tagInfo) return null
                  // Show as visible only if tag is in visible set (or if no filtering is active)
                  const isVisible = visibleTags.size === 0 || visibleTags.has(tagId as string)
                  return (
                    <LegendItem
                      key={tagId as string}
                      style={{
                        cursor: 'pointer',
                        opacity: isVisible ? 1 : 0.4,
                        transition: 'opacity 0.2s ease',
                        padding: '0.15rem',
                        borderRadius: '0.25rem',
                        marginLeft: '-0.15rem',
                        marginRight: '-0.15rem',
                      }}
                      onClick={() => handleTagToggle(tagId as string)}
                      title={`Click to ${isVisible ? 'hide' : 'show'} nodes with "${
                        tagInfo.name
                      }" tag`}
                    >
                      <LegendColor
                        color={tagInfo.color}
                        style={{
                          opacity: isVisible ? 1 : 0.3,
                          border: isVisible
                            ? '1px solid rgba(255, 255, 255, 0.3)'
                            : '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      />
                      <span
                        style={{
                          textDecoration: isVisible ? 'none' : 'line-through',
                        }}
                      >
                        {tagInfo.name}
                      </span>
                    </LegendItem>
                  )
                })
                .filter(Boolean)}
              <LegendItem
                style={{
                  cursor: 'pointer',
                  opacity: visibleTags.size === 0 || visibleTags.has('no-tags') ? 1 : 0.4,
                  transition: 'opacity 0.2s ease',
                  padding: '0.15rem',
                  borderRadius: '0.25rem',
                  marginLeft: '-0.15rem',
                  marginRight: '-0.15rem',
                }}
                onClick={() => handleTagToggle('no-tags')}
                title={`Click to ${
                  visibleTags.size === 0 || visibleTags.has('no-tags') ? 'hide' : 'show'
                } nodes with no tags`}
              >
                <LegendColor
                  color="#6c757d"
                  style={{
                    opacity: visibleTags.size === 0 || visibleTags.has('no-tags') ? 1 : 0.3,
                    border:
                      visibleTags.size === 0 || visibleTags.has('no-tags')
                        ? '1px solid rgba(255, 255, 255, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                />
                <span
                  style={{
                    textDecoration:
                      visibleTags.size === 0 || visibleTags.has('no-tags')
                        ? 'none'
                        : 'line-through',
                  }}
                >
                  No tags
                </span>
              </LegendItem>
            </LegendItemsContainer>
          </Legend>
        )

      case 'source':
        return (
          <Legend $expanded={expandedLegend}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>By Source</div>
            {getUniqueSources.length === 0 ? (
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>No source data available</div>
            ) : (
              <>
                <LegendItemsContainer $expanded={expandedLegend}>
                  {(expandedLegend ? getUniqueSources : getUniqueSources.slice(0, 6)).map(
                    (
                      source // Show 6 sources or all if expanded
                    ) => (
                      <LegendItem key={source}>
                        <LegendColor color={getSourceColor(source)} />
                        <span>{source}</span>
                      </LegendItem>
                    )
                  )}
                  <LegendItem>
                    <LegendColor color="#6c757d" />
                    <span>Unknown source</span>
                  </LegendItem>
                </LegendItemsContainer>
                {getUniqueSources.length > 6 && (
                  <div
                    style={{
                      fontSize: '0.65rem',
                      opacity: 0.7,
                      marginTop: '0.25rem',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '0.25rem',
                      marginLeft: '-0.25rem',
                      marginRight: '-0.25rem',
                      transition: 'opacity 0.2s ease, background-color 0.2s ease',
                    }}
                    onClick={() => setExpandedLegend(!expandedLegend)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1'
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.7'
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                    title={`Click to ${expandedLegend ? 'collapse' : 'expand'} legend`}
                  >
                    {expandedLegend
                      ? `▲ Show less (${getUniqueSources.length} total)`
                      : `▼ +${getUniqueSources.length - 6} more sources`}
                  </div>
                )}
              </>
            )}
          </Legend>
        )

      case 'size':
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>By Image Size</div>
            <LegendItem>
              <LegendColor color="#e83e8c" />
              <span>Huge (≥2048px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#6f42c1" />
              <span>Very Large (1024-2047px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#17a2b8" />
              <span>Large (512-1023px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#007bff" />
              <span>Medium (256-511px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#20c997" />
              <span>Small (128-255px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#fd7e14" />
              <span>Tiny (&lt;128px)</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#6c757d" />
              <span>Unknown</span>
            </LegendItem>
          </Legend>
        )

      case 'outliers':
        const outlierCount = detectOutliers.size
        const totalCount = activeContentData?.content?.length || 0
        return (
          <Legend>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Outlier Detection
              <div
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 'normal',
                  opacity: 0.8,
                  marginTop: '0.2rem',
                }}
              >
                {outlierCount} of {totalCount} items flagged
              </div>
            </div>
            <LegendItem>
              <LegendColor color="#dc3545" />
              <span>Outliers ({outlierCount})</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#28a745" />
              <span>Normal ({totalCount - outlierCount})</span>
            </LegendItem>
            <div
              style={{
                fontSize: '0.65rem',
                opacity: 0.8,
                marginTop: '0.5rem',
                lineHeight: '1.3',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Detection Methods:</div>
              <div>• Vector isolation (PCA space)</div>
              <div>• Unusual image dimensions</div>
              <div>• High annotation density</div>
              <div>• Diverse tag combinations</div>
            </div>
          </Legend>
        )

      default:
        return null
    }
  }, [
    visualizationMode,
    processedContentData?.content,
    expandedLegend,
    visibleTags,
    getUniqueSources,
    detectOutliers.size,
    activeContentData?.content?.length,
    getMostDominantTag,
    tagIdToNameAndColor,
    getSourceColor,
  ])

  const handleViewContent = useCallback(() => {
    if (selectedNodes.size === 0) return

    // Save current viewport position and zoom
    if (reactFlowInstance) {
      const viewport = reactFlowInstance.getViewport()
      setSavedViewport(viewport)
    }

    // Show the content viewer with current data (don't refetch to avoid viewport changes)
    setShowContentViewer(true)
  }, [selectedNodes, reactFlowInstance])

  // Cleanup old localStorage data on mount
  useEffect(() => {
    try {
      const keys = Object.keys(localStorage)
      const displacementKeys = keys.filter((key) => key.startsWith('displacement-chart-'))

      // Keep only the last 5 project data entries to prevent bloat
      if (displacementKeys.length > 5) {
        const keysToRemove = displacementKeys.slice(0, displacementKeys.length - 5)
        keysToRemove.forEach((key) => safeLocalStorage.removeItem(key))
      }
    } catch (error) {
      console.warn('Failed to cleanup old displacement chart data:', error)
    }
  }) // Run only on mount

  // Debounced stats update to prevent constant card resizing
  useEffect(() => {
    if (!onStatsUpdate || !processedContentData || isDrawing) return

    const timeoutId = setTimeout(
      () => {
        onStatsUpdate({
          contentData: processedContentData,
          selectedNodes,
          visualizationMode,
          renderMode,
          tagIdToNameAndColor,
          onViewContent: handleViewContent,
          outlierReasons: visualizationMode === 'outliers' ? outlierReasons : undefined,
        })
      },
      isDrawing ? 0 : 100
    ) // Immediate update when not drawing, debounced otherwise

    return () => clearTimeout(timeoutId)
  }, [
    onStatsUpdate,
    processedContentData,
    selectedNodes,
    visualizationMode,
    renderMode,
    tagIdToNameAndColor,
    handleViewContent,
    isDrawing,
    outlierReasons,
  ])

  // Handle node clicks for cluster expansion
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: any) => {
      if (node.type === 'cluster') {
        event.preventDefault()
        event.stopPropagation()
        handleClusterClick(node.id)
      }
    },
    [handleClusterClick]
  )

  // State to preserve viewport when viewing content and track initialization
  const [savedViewport, setSavedViewport] = useState<{
    x: number
    y: number
    zoom: number
  } | null>(null)
  const [hasInitializedView, setHasInitializedView] = useState(false)

  // Ensure fitView happens automatically when nodes are first loaded or updated
  React.useEffect(() => {
    if (reactFlowInstance && nodesState.length > 0 && !isDrawing && !showContentViewer) {
      const timer = setTimeout(
        () => {
          reactFlowInstance.fitView({
            padding: 0.2, // 20% padding for visual breathing room
            includeHiddenNodes: false,
            minZoom: 0.1,
            maxZoom: 2,
            duration: hasInitializedView ? 400 : 800, // Faster updates after initial load
          })
          if (!hasInitializedView) {
            setHasInitializedView(true)
          }
        },
        hasInitializedView ? 100 : 300
      ) // Faster updates after initial load

      return () => clearTimeout(timer)
    }
  }, [reactFlowInstance, nodesState.length, hasInitializedView, isDrawing, showContentViewer])

  // Track zoom changes for dynamic node spacing
  const onMove = useCallback(
    (event: any, viewport: any) => {
      const newZoom = viewport.zoom
      if (Math.abs(newZoom - currentZoom) > 0.05) {
        // Update when zoom changes by more than 5% for more responsive spacing
        setCurrentZoom(newZoom)
      }
    },
    [currentZoom]
  )

  // Auto-fit when visualization mode or render mode changes
  React.useEffect(() => {
    if (reactFlowInstance && nodesState.length > 0) {
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.1,
          maxZoom: 2,
          duration: 600,
        })
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [visualizationMode, renderMode, reactFlowInstance, nodesState.length])

  // Check vectorization status
  const vectorizationStatus = project?.vectorization

  // Helper function to get vector status display info
  const getVectorStatusInfo = (status: number) => {
    switch (status) {
      case 0: // Unknown/Not Started
        return {
          label: 'Not Started',
          color: '#ffc107', // Yellow/Amber
          description: 'Vectorization has not been started yet.',
          isInProgress: false,
          showProgress: false,
        }
      case 1: // Initialized
        return {
          label: 'Initializing',
          color: '#0dcaf0', // Blue
          description: 'Preparing to vectorize images...',
          isInProgress: true,
          showProgress: false,
        }
      case 2: // Running
        return {
          label: 'Processing',
          color: '#0d6efd', // Blue
          description:
            'Processing images to generate vector embeddings for displacement visualization...',
          isInProgress: true,
          showProgress: true,
        }
      case 3: // Complete
        return {
          label: 'Complete',
          color: '#28a745', // Green
          description: 'All images have been successfully vectorized.',
          isInProgress: false,
          showProgress: true,
        }
      case 4: // Failed
        return {
          label: 'Failed',
          color: '#dc3545', // Red
          description: 'Vectorization failed after maximum retries. Please try again.',
          isInProgress: false,
          showProgress: true,
        }
      default:
        return {
          label: 'Unknown',
          color: '#6c757d', // Gray
          description: 'Status unknown.',
          isInProgress: false,
          showProgress: false,
        }
    }
  }

  const statusInfo = vectorizationStatus
    ? getVectorStatusInfo(vectorizationStatus.vector_status)
    : null
  const isVectorizingProject = statusInfo?.isInProgress || false
  const vectorizationProgress =
    vectorizationStatus && project?.count && project.count > 0
      ? (vectorizationStatus.total_vectors_processed / project.count) * 100
      : 0

  // Tab state for switching between vectorization progress and chart
  const [activeTab, setActiveTab] = useState<string>('chart')

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false)

  // Overlap visualization state
  const [highlightedOverlaps, setHighlightedOverlaps] = useState<
    Array<{ tag1: string; tag2: string }>
  >([])

  // Calculate dataset insights for model training
  // Calculate dataset health score (0-100)
  const calculateHealthScore = useCallback((insights: any) => {
    if (!insights) return 0

    let score = 100

    // Deduct points for warnings based on severity
    insights.warnings.forEach((warning: any) => {
      if (warning.severity === 'high') score -= 20
      else if (warning.severity === 'medium') score -= 10
      else if (warning.severity === 'low') score -= 5
    })

    // Bonus points for positive recommendations
    if (insights.recommendations.some((r: any) => r.type === 'good_separation')) {
      score += 5
    }
    if (insights.recommendations.some((r: any) => r.type === 'quality')) {
      score += 5
    }

    // Ensure score is between 0 and 100
    return Math.max(0, Math.min(100, score))
  }, [])

  // Handle clicking on individual pair
  const handleShowPair = useCallback(
    (pair: { tag1: string; tag2: string; distance: number }) => {
      // Switch to annotation visualization
      setVisualizationMode('annotation')

      // Store just this one pair for the clear button
      setHighlightedOverlaps([pair])

      // Find tag IDs from names
      const tagIdsToShow = new Set<string>()
      Object.entries(tagIdToNameAndColor).forEach(([tagId, info]) => {
        if (info.name === pair.tag1 || info.name === pair.tag2) {
          tagIdsToShow.add(tagId)
        }
      })

      setVisibleTags(tagIdsToShow)

      // Fit view to show the filtered content
      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.fitView({
            padding: 0.3,
            duration: 800,
          })
        }
      }, 100)
    },
    [tagIdToNameAndColor, reactFlowInstance]
  )

  // Get color for a class name
  const getClassColor = useCallback(
    (className: string) => {
      const entry = Object.entries(tagIdToNameAndColor).find(([_, info]) => info.name === className)
      return entry ? entry[1].color : '#6c757d'
    },
    [tagIdToNameAndColor]
  )

  // Get severity label and color from distance
  const getSeverityFromDistance = useCallback(
    (distance: number): { label: string; color: string } => {
      if (distance < 50) return { label: 'High', color: '#dc3545' }
      if (distance < 100) return { label: 'Medium', color: '#ffc107' }
      if (distance < 200) return { label: 'Low', color: '#17a2b8' }
      return { label: 'Good', color: '#28a745' }
    },
    []
  )

  const datasetInsights = useMemo(() => {
    if (!processedContentData?.content || !tagsData?.tags) return null

    const contentArray = processedContentData.content as any[]
    const insights: any = {
      totalImages: contentArray.length,
      warnings: [],
      recommendations: [],
      metrics: {},
    }

    // 1. Check class balance
    const tagCounts: Record<string, number> = {}
    contentArray.forEach((item: any) => {
      const dominantTag = getMostDominantTag(item.annotation)
      if (dominantTag) {
        tagCounts[dominantTag] = (tagCounts[dominantTag] || 0) + 1
      } else {
        tagCounts['unlabeled'] = (tagCounts['unlabeled'] || 0) + 1
      }
    })

    const maxCount = Math.max(...Object.values(tagCounts))
    const minCount = Math.min(...Object.values(tagCounts).filter((c) => c > 0))
    const imbalanceRatio = maxCount / (minCount || 1)

    // Find which tags are most and least common
    const mostCommonTag = Object.entries(tagCounts).find(([_, count]) => count === maxCount)?.[0]
    const leastCommonTag = Object.entries(tagCounts).find(([_, count]) => count === minCount)?.[0]

    const mostCommonName =
      mostCommonTag && mostCommonTag !== 'unlabeled'
        ? tagIdToNameAndColor[mostCommonTag]?.name || 'Unknown'
        : 'Unlabeled'
    const leastCommonName =
      leastCommonTag && leastCommonTag !== 'unlabeled'
        ? tagIdToNameAndColor[leastCommonTag]?.name || 'Unknown'
        : 'Unlabeled'

    insights.metrics.classBalance = {
      mostCommon: maxCount,
      leastCommon: minCount,
      ratio: imbalanceRatio,
      mostCommonTag: mostCommonName,
      leastCommonTag: leastCommonName,
    }

    if (imbalanceRatio > 10) {
      insights.warnings.push({
        type: 'imbalance',
        severity: 'high',
        message: `Severe class imbalance: "${mostCommonName}" has ${maxCount} images vs "${leastCommonName}" with ${minCount}`,
        impact: `Model will be heavily biased toward "${mostCommonName}" and may rarely predict "${leastCommonName}"`,
        action: `Add more "${leastCommonName}" images or use class weights during training`,
        details: `${imbalanceRatio.toFixed(1)}:1 ratio`,
        metrics: {
          mostCommonTag: mostCommonName,
          leastCommonTag: leastCommonName,
          ratio: imbalanceRatio,
          maxCount,
          minCount,
        },
      })
    } else if (imbalanceRatio > 3) {
      insights.warnings.push({
        type: 'imbalance',
        severity: 'medium',
        message: `Moderate imbalance: "${mostCommonName}" (${maxCount}) vs "${leastCommonName}" (${minCount})`,
        impact: `Model may perform worse on "${leastCommonName}"`,
        action: `Consider augmenting "${leastCommonName}" data or using class weights`,
        details: `${imbalanceRatio.toFixed(1)}:1 ratio`,
        metrics: {
          mostCommonTag: mostCommonName,
          leastCommonTag: leastCommonName,
          ratio: imbalanceRatio,
          maxCount,
          minCount,
        },
      })
    }

    // 2. Check for unlabeled data
    const unlabeledCount = tagCounts['unlabeled'] || 0
    const unlabeledPercent = (unlabeledCount / contentArray.length) * 100

    if (unlabeledCount > 0) {
      insights.warnings.push({
        type: 'unlabeled',
        severity: unlabeledPercent > 20 ? 'high' : 'low',
        message: `${unlabeledCount} images (${unlabeledPercent.toFixed(1)}%) are unlabeled`,
        impact: unlabeledPercent > 20 ? 'Significant training data is unused' : 'Minor data loss',
        action: 'Label remaining images or remove them from training set',
      })
    }

    // 3. Analyze cluster separation (class separability)
    if (visualizationMode === 'annotation' && nodes.length > 0) {
      // Calculate average intra-class distance vs inter-class distance
      const tagPositions: Record<string, { x: number; y: number }[]> = {}

      nodes.forEach((node: any) => {
        if (node.type === 'dataPoint' && node.data.content) {
          const tag = getMostDominantTag(node.data.content.annotation) || 'unlabeled'
          if (!tagPositions[tag]) tagPositions[tag] = []
          tagPositions[tag].push(node.position)
        }
      })

      // Check if classes are well-separated
      const tagCentroids: Record<string, { x: number; y: number }> = {}
      Object.entries(tagPositions).forEach(([tag, positions]) => {
        if (positions.length > 0) {
          tagCentroids[tag] = {
            x: positions.reduce((sum, p) => sum + p.x, 0) / positions.length,
            y: positions.reduce((sum, p) => sum + p.y, 0) / positions.length,
          }
        }
      })

      const tagNames = Object.keys(tagCentroids)
      if (tagNames.length >= 2) {
        // Calculate all pairwise centroid distances
        const allPairs: Array<{ tag1: string; tag2: string; distance: number }> = []
        let minCentroidDist = Infinity

        for (let i = 0; i < tagNames.length; i++) {
          for (let j = i + 1; j < tagNames.length; j++) {
            const c1 = tagCentroids[tagNames[i]]
            const c2 = tagCentroids[tagNames[j]]
            const dist = Math.sqrt(Math.pow(c1.x - c2.x, 2) + Math.pow(c1.y - c2.y, 2))

            const tag1Name = tagIdToNameAndColor[tagNames[i]]?.name || 'Unknown'
            const tag2Name = tagIdToNameAndColor[tagNames[j]]?.name || 'Unknown'
            allPairs.push({
              tag1: tag1Name,
              tag2: tag2Name,
              distance: dist,
            })

            minCentroidDist = Math.min(minCentroidDist, dist)
          }
        }

        // Sort by distance to find most problematic pairs
        allPairs.sort((a, b) => a.distance - b.distance)

        insights.metrics.classSeparation = minCentroidDist

        // Store all pairs for detailed view
        insights.metrics.allOverlappingPairs = allPairs.filter((p) => p.distance < 200)

        if (minCentroidDist < 100) {
          // High severity - heavily overlapping
          const problematicPairs = allPairs.filter((p) => p.distance < 100)

          insights.warnings.push({
            type: 'overlap',
            severity: 'high',
            message: `${problematicPairs.length} class pair${
              problematicPairs.length > 1 ? 's' : ''
            } overlapping`,
            impact: 'Model will struggle to distinguish between these classes',
            action: 'Click any pair below to view on chart',
            overlappingPairs: problematicPairs,
          })
        } else if (minCentroidDist < 200) {
          // Medium severity - somewhat close
          const closeishPairs = allPairs.filter((p) => p.distance < 200)

          insights.warnings.push({
            type: 'overlap',
            severity: 'medium',
            message: `${closeishPairs.length} class pair${
              closeishPairs.length > 1 ? 's' : ''
            } could be better separated`,
            impact: 'Model may occasionally confuse these classes',
            action: 'Click any pair below to view on chart',
            overlappingPairs: closeishPairs,
          })
        } else {
          insights.recommendations.push({
            type: 'good_separation',
            message: 'Classes are well-separated',
            benefit: 'Model should learn distinct features for each class',
          })
        }
      }
    }

    // 4. Check dataset size
    if (contentArray.length < 100) {
      insights.warnings.push({
        type: 'small_dataset',
        severity: 'high',
        message: 'Very small dataset',
        impact: 'High risk of overfitting and poor generalization',
        action: 'Collect more data or use heavy data augmentation',
      })
    } else if (contentArray.length < 500) {
      insights.warnings.push({
        type: 'small_dataset',
        severity: 'medium',
        message: 'Small dataset',
        impact: 'Risk of overfitting',
        action: 'Use data augmentation and regularization techniques',
      })
    }

    // 5. Check for outliers
    const outlierPercent = (detectOutliers.size / contentArray.length) * 100
    if (outlierPercent > 10) {
      insights.warnings.push({
        type: 'outliers',
        severity: 'medium',
        message: `${detectOutliers.size} outliers detected (${outlierPercent.toFixed(1)}%)`,
        impact: 'Outliers may be mislabeled or corrupted data',
        action: 'Review outliers using "Outliers" visualization mode',
      })
    }

    // 6. General recommendations
    if (insights.warnings.length === 0) {
      insights.recommendations.push({
        type: 'quality',
        message: 'Dataset looks healthy!',
        benefit: 'No major issues detected - ready for training',
      })
    }

    return insights
  }, [
    processedContentData,
    tagsData,
    visualizationMode,
    nodes,
    getMostDominantTag,
    detectOutliers,
    tagIdToNameAndColor,
  ])

  return (
    <ChartContainer style={{ display: 'flex', flexDirection: 'column' }}>
      <ChartHeader>
        <ChartTitle>
          <Activity size={16} />
          Displacement Chart
          <button
            onClick={() => setShowHelpModal(true)}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              color: '#ffffff',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '0.5rem',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
              e.currentTarget.style.borderColor = '#667eea'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)'
            }}
            title="What is this? Click for help"
          >
            <HelpCircle size={14} />
          </button>
          {/* Dataset Filter Dropdown */}
          {availableDatasets.length > 0 && (
            <Dropdown
              show={showDatasetDropdown}
              onToggle={(isOpen) => setShowDatasetDropdown(isOpen)}
              style={{ marginLeft: '0.5rem' }}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.85rem',
                  padding: '0.4rem 0.8rem',
                  background:
                    selectedDatasets.length > 0 ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
                  borderColor: selectedDatasets.length > 0 ? '#667eea' : 'rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                }}
              >
                <Database size={14} />
                {selectedDatasets.length === 0
                  ? 'All Datasets'
                  : selectedDatasets.length === 1
                  ? selectedDatasets[0]
                  : `${selectedDatasets.length} Datasets`}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  backgroundColor: 'rgba(45, 55, 72, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                <Dropdown.Item
                  onClick={() => setSelectedDatasets([])}
                  style={{
                    color: selectedDatasets.length === 0 ? '#667eea' : '#ffffff',
                    backgroundColor: 'transparent',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                  }}
                >
                  <strong>All Datasets</strong> ({availableDatasets.length})
                </Dropdown.Item>
                <Dropdown.Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                {availableDatasets.map((dataset) => (
                  <Dropdown.Item
                    key={dataset}
                    onClick={() => {
                      if (selectedDatasets.includes(dataset)) {
                        setSelectedDatasets(selectedDatasets.filter((d) => d !== dataset))
                      } else {
                        setSelectedDatasets([...selectedDatasets, dataset])
                      }
                    }}
                    style={{
                      color: selectedDatasets.includes(dataset) ? '#667eea' : '#ffffff',
                      backgroundColor: 'transparent',
                      fontSize: '0.85rem',
                      padding: '0.5rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDatasets.includes(dataset)}
                      readOnly
                      style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {dataset}
                  </Dropdown.Item>
                ))}
                {selectedDatasets.length > 0 && (
                  <>
                    <Dropdown.Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Dropdown.Item
                      onClick={() => setSelectedDatasets([])}
                      style={{
                        color: '#dc3545',
                        backgroundColor: 'transparent',
                        fontSize: '0.85rem',
                        padding: '0.5rem 1rem',
                      }}
                    >
                      Clear Selection
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
          {vectorizationStatus && (
            <small style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '0.5rem' }}>
              ({allContentData?.content?.length || 0} items
              {selectedDatasets.length > 0 && ' (filtered)'})
            </small>
          )}
        </ChartTitle>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <GenerateButton
            onClick={handleGeneratePCA}
            disabled={isLoading_ || !projectId}
            title="Generate or refresh the 2D visualization from your vectorized images"
          >
            {isGeneratingPCA ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {processedContentData ? 'Regenerating PCA...' : 'Generating PCA...'}
              </>
            ) : processedContentData ? (
              'Regenerate PCA'
            ) : (
              'Generate PCA'
            )}
          </GenerateButton>
          {vectorizationStatus &&
            vectorizationStatus.vector_status === 3 &&
            vectorizationStatus.total_vectors_failed > 0 && (
              <GenerateButton
                onClick={handleVectorizeProject}
                disabled={isLoading_ || !projectId}
                title="Retry vectorization for failed items"
              >
                {isVectorizing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Retrying...
                  </>
                ) : (
                  `Retry Failed (${vectorizationStatus.total_vectors_failed})`
                )}
              </GenerateButton>
            )}
        </div>
      </ChartHeader>

      <TabsContainer>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'vectorization')}
          className="mb-0"
        >
          <Tab
            eventKey="vectorization"
            title={
              <span className="d-flex align-items-center gap-2">
                {statusInfo?.isInProgress && (
                  <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                )}
                <span>Vectorization Progress</span>
                {statusInfo && (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: statusInfo.color,
                      fontSize: '0.65rem',
                      padding: '0.25rem 0.5rem',
                    }}
                  >
                    {statusInfo.label}
                  </span>
                )}
              </span>
            }
          >
            {vectorizationStatus ? (
              <VectorizationProgress>
                <ProgressTitle>
                  {statusInfo?.isInProgress && <Spinner animation="border" size="sm" />}
                  <span style={{ color: statusInfo?.color || '#fff' }}>
                    {statusInfo?.label || 'Unknown Status'}
                  </span>
                </ProgressTitle>
                <p style={{ color: '#adb5bd', margin: 0 }}>
                  {statusInfo?.description || 'No status information available.'}
                </p>
                <div style={{ width: '100%', maxWidth: '500px' }}>
                  <ProgressBarContainer>
                    <ProgressBarFill progress={vectorizationProgress} />
                  </ProgressBarContainer>
                  <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#adb5bd' }}>
                    {vectorizationProgress.toFixed(1)}% complete
                  </div>
                </div>
                <ProgressStats>
                  <div className="stat">
                    <div className="label">Total</div>
                    <div className="value">{project?.count || 0}</div>
                  </div>
                  <div className="stat">
                    <div className="label">Processed</div>
                    <div className="value">{vectorizationStatus.total_vectors_processed}</div>
                  </div>
                  <div className="stat">
                    <div className="label">Succeeded</div>
                    <div className="value" style={{ color: '#28a745' }}>
                      {vectorizationStatus.total_vectors_succeeded}
                    </div>
                  </div>
                  <div className="stat">
                    <div className="label">Failed</div>
                    <div
                      className="value"
                      style={{
                        color: vectorizationStatus.total_vectors_failed > 0 ? '#dc3545' : '#adb5bd',
                      }}
                    >
                      {vectorizationStatus.total_vectors_failed}
                    </div>
                  </div>
                </ProgressStats>
                {isVectorizingProject && (
                  <p style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
                    This page will automatically update when vectorization is complete.
                  </p>
                )}
                {vectorizationStatus &&
                  vectorizationStatus.vector_status === 3 &&
                  vectorizationStatus.total_vectors_failed > 0 && (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={handleVectorizeProject}
                      style={{ marginTop: '1rem' }}
                    >
                      Retry Failed Items ({vectorizationStatus.total_vectors_failed})
                    </Button>
                  )}
              </VectorizationProgress>
            ) : (
              <LoadingContainer>
                <Activity size={48} color="#114ee0" />
                <p>No vectorization data available</p>
                <Button variant="outline-light" size="sm" onClick={handleVectorizeProject}>
                  Start Vectorization
                </Button>
              </LoadingContainer>
            )}
          </Tab>

          <Tab eventKey="chart" title="Displacement Chart">
            {error && (
              <Alert variant="danger" className="m-3 mb-0">
                {error}
              </Alert>
            )}

            {contentFetchError && !isLoadingAllContent ? (
              <div style={{ padding: '2rem' }}>
                <Alert variant="danger">
                  {contentFetchError}
                  <div style={{ marginTop: '1rem' }}>
                    <Button variant="outline-light" size="sm" onClick={fetchAllContentPages}>
                      Retry
                    </Button>
                  </div>
                </Alert>
              </div>
            ) : null}

            {!showChart ? (
              <LoadingContainer>
                <Activity size={48} color="#114ee0" />
                <p>Click "Generate PCA" to create displacement visualization</p>
              </LoadingContainer>
            ) : isLoading_ ? (
              <LoadingContainer>
                <Spinner animation="border" variant="primary" />
                <p>Loading displacement data...</p>
              </LoadingContainer>
            ) : (
              <DashboardContainer>
                {/* Left Panel: Chart */}
                <ChartPanel>
                  <FlowContainer
                    style={{ position: 'relative', flex: 1, minHeight: 0 }}
                    className={lassoMode ? 'lasso-mode' : ''}
                  >
                    <div style={{ position: 'relative', height: '100%' }}>
                      {/* Button Overlay */}
                      <ButtonOverlay>
                        {showChart && (
                          <>
                            <div className="button-row">
                              {/* Performance Mode Selector */}
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  className="dropdown-toggle"
                                  title="Performance: Choose how to render your data (Clustered groups similar images)"
                                >
                                  ⚡{' '}
                                  {renderMode === 'full'
                                    ? 'Full'
                                    : renderMode === 'sample'
                                    ? `Sample (${sampleSize})`
                                    : renderMode === 'clustered'
                                    ? 'Clustered'
                                    : 'Viewport'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  style={{
                                    backgroundColor: 'rgba(45, 55, 72, 0.95)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(8px)',
                                  }}
                                >
                                  <Dropdown.Item
                                    onClick={() => setRenderMode('full')}
                                    style={{
                                      color: renderMode === 'full' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Full Rendering ({allContentData?.content?.length || 0} items)
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setRenderMode('clustered')}
                                    style={{
                                      color: renderMode === 'clustered' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Clustered View (Smart grouping by proximity & color)
                                  </Dropdown.Item>
                                  <Dropdown.Divider
                                    style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}
                                  />
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRenderMode('sample')
                                      setSampleSize(1000)
                                    }}
                                    style={{
                                      color:
                                        renderMode === 'sample' && sampleSize === 1000
                                          ? '#fbbf24'
                                          : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Sample: 1,000 items
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRenderMode('sample')
                                      setSampleSize(2000)
                                    }}
                                    style={{
                                      color:
                                        renderMode === 'sample' && sampleSize === 2000
                                          ? '#fbbf24'
                                          : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Sample: 2,000 items
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRenderMode('sample')
                                      setSampleSize(3000)
                                    }}
                                    style={{
                                      color:
                                        renderMode === 'sample' && sampleSize === 3000
                                          ? '#fbbf24'
                                          : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Sample: 3,000 items
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setRenderMode('sample')
                                      setSampleSize(5000)
                                    }}
                                    style={{
                                      color:
                                        renderMode === 'sample' && sampleSize === 5000
                                          ? '#fbbf24'
                                          : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    Sample: 5,000 items
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>

                              {/* Visualization Mode Selector */}
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  className="dropdown-toggle"
                                  title="Visualization: Change how images are colored (By Tag shows annotation categories)"
                                >
                                  <Palette size={14} className="me-1" />
                                  {visualizationMode === 'default' && 'Default'}
                                  {visualizationMode === 'annotation' && 'By Tag'}
                                  {visualizationMode === 'source' && 'By Source'}
                                  {visualizationMode === 'size' && 'By Size'}
                                  {visualizationMode === 'outliers' && 'Outliers'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  style={{
                                    backgroundColor: 'rgba(45, 55, 72, 0.95)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(8px)',
                                  }}
                                >
                                  <Dropdown.Item
                                    onClick={() => setVisualizationMode('default')}
                                    style={{
                                      color:
                                        visualizationMode === 'default' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Activity size={14} className="me-2" />
                                    Default (Annotated/Unannotated)
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setVisualizationMode('annotation')}
                                    style={{
                                      color:
                                        visualizationMode === 'annotation' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Filter size={14} className="me-2" />
                                    By Most Dominant Tag
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setVisualizationMode('source')}
                                    style={{
                                      color: visualizationMode === 'source' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Eye size={14} className="me-2" />
                                    By Data Source
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setVisualizationMode('size')}
                                    style={{
                                      color: visualizationMode === 'size' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <X size={14} className="me-2" />
                                    By Image Size
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setVisualizationMode('outliers')}
                                    style={{
                                      color:
                                        visualizationMode === 'outliers' ? '#fbbf24' : '#ffffff',
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Activity size={14} className="me-2" />
                                    Outlier Detection
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <div className="button-row">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  if (reactFlowInstance && nodesState.length > 0) {
                                    reactFlowInstance.fitView({
                                      padding: 0.2,
                                      includeHiddenNodes: false,
                                      minZoom: 0.1,
                                      maxZoom: 2,
                                      duration: 600,
                                    })
                                  }
                                }}
                                className="btn"
                                title="Center and fit all nodes in view"
                              >
                                Fit View
                              </Button>

                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowMiniMap(!showMiniMap)}
                                className="btn"
                                title="Toggle mini-map overview in the corner"
                              >
                                {showMiniMap ? 'Hide Map' : 'Show Map'}
                              </Button>

                              <Button
                                variant={lassoMode ? 'warning' : 'outline-secondary'}
                                size="sm"
                                onClick={() => {
                                  setLassoMode(!lassoMode)
                                  setSelectedNodes(new Set())
                                  setLassoPath([])
                                  setIsDrawing(false)
                                }}
                                className="btn"
                                title="Draw around images to select multiple at once"
                              >
                                {lassoMode ? 'Exit Lasso' : 'Lasso Select'}
                              </Button>

                              {/* View Selected Images Button - shows when images are selected */}
                              {selectedContentItems.length > 0 && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleViewContent}
                                  className="btn"
                                  title="View selected images in a grid"
                                  style={{ fontWeight: 'bold' }}
                                >
                                  View {selectedContentItems.length} Image
                                  {selectedContentItems.length > 1 ? 's' : ''}
                                </Button>
                              )}

                              {/* Reset Clusters Button - only show when in clustered mode and clusters are expanded */}
                              {renderMode === 'clustered' && isClusterExpanded && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={resetClusterExpansions}
                                  className="btn"
                                  title="Reset all expanded clusters"
                                >
                                  Reset Clusters
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </ButtonOverlay>

                      {/* Legend */}
                      {renderLegend()}

                      {/* Lasso drawing overlay - only blocks when actively drawing */}
                      {lassoMode && isDrawing && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1000,
                            cursor: 'crosshair',
                            pointerEvents: 'auto',
                            background: 'transparent',
                          }}
                          onMouseMove={handleMouseMove}
                          onMouseUp={handleMouseUp}
                          onContextMenu={handleContextMenu}
                        />
                      )}

                      {/* Optimized lasso path visualization */}
                      {lassoMode && lassoPath.length > 1 && (
                        <svg
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 999,
                          }}
                        >
                          <path
                            d={`M ${lassoPath
                              .map((p) => {
                                const containerElement = document.querySelector(
                                  '.react-flow'
                                ) as HTMLElement
                                if (!containerElement) return `${p.x},${p.y}`
                                const rect = containerElement.getBoundingClientRect()
                                const containerX = p.x - rect.left
                                const containerY = p.y - rect.top
                                return `${containerX},${containerY}`
                              })
                              .join(' L ')} ${isDrawing ? '' : 'Z'}`}
                            stroke="#fbbf24"
                            strokeWidth="2"
                            fill={isDrawing ? 'none' : 'rgba(251, 191, 36, 0.1)'}
                            strokeDasharray="5,5"
                          />
                        </svg>
                      )}

                      <div
                        onMouseDown={(e) => {
                          if (lassoMode && e.button === 0) {
                            // Left mouse button in lasso mode
                            handleMouseDown(e)
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          width: '100%',
                          height: '100%',
                          cursor: lassoMode ? 'crosshair' : 'default',
                        }}
                      >
                        <ReactFlow
                          nodes={nodesState}
                          edges={edgesState}
                          onNodesChange={onNodesChange}
                          onEdgesChange={onEdgesChange}
                          nodeTypes={nodeTypes}
                          onInit={(instance) => {
                            setReactFlowInstance(instance)
                            // Force fit view immediately on init
                            if (nodesState.length > 0) {
                              setTimeout(() => {
                                instance.fitView({
                                  padding: 0.2,
                                  includeHiddenNodes: false,
                                  minZoom: 0.1,
                                  maxZoom: 2,
                                  duration: 800,
                                })
                              }, 100)
                            }
                          }}
                          attributionPosition="bottom-left"
                          proOptions={{ hideAttribution: true }}
                          panOnDrag={lassoMode ? [1] : true} // Allow middle mouse panning, disable left drag in lasso mode
                          zoomOnScroll={true} // Always allow scroll wheel zoom
                          zoomOnPinch={true} // Always allow pinch zoom
                          zoomOnDoubleClick={!lassoMode} // Disable double-click zoom when in lasso mode
                          onNodeClick={onNodeClick}
                          minZoom={0.1} // Lower minimum zoom to see more
                          maxZoom={4} // More reasonable maximum zoom
                          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
                          onMove={onMove}
                          fitView
                          style={{
                            width: '100%',
                            height: '100%',
                            cursor: lassoMode ? 'crosshair !important' : 'default',
                          }}
                        >
                          <Background color="#333" gap={20} />
                          <Controls />
                          {showMiniMap && (
                            <MiniMap
                              nodeColor={(node) => node.data.color}
                              nodeStrokeWidth={3}
                              zoomable
                              pannable
                              style={{
                                backgroundColor: '#1a1a1a',
                                border: '1px solid #333',
                              }}
                            />
                          )}
                        </ReactFlow>
                      </div>
                    </div>
                  </FlowContainer>
                </ChartPanel>

                {/* Right Panel: Insights */}
                {datasetInsights && (
                  <InsightsPanel>
                    <InsightsScrollContainer>
                      {/* Status Bar */}
                      <StatusBar
                        status={
                          datasetInsights.warnings.filter((w: any) => w.severity === 'high')
                            .length > 0
                            ? 'error'
                            : datasetInsights.warnings.length > 0
                            ? 'warning'
                            : 'ready'
                        }
                      >
                        <StatusDot
                          color={
                            datasetInsights.warnings.filter((w: any) => w.severity === 'high')
                              .length > 0
                              ? '#dc3545'
                              : datasetInsights.warnings.length > 0
                              ? '#ffc107'
                              : '#28a745'
                          }
                        />
                        <StatusText>
                          {datasetInsights.warnings.filter((w: any) => w.severity === 'high')
                            .length > 0
                            ? 'Issues detected'
                            : datasetInsights.warnings.length > 0
                            ? 'Minor improvements needed'
                            : 'Ready to train'}
                        </StatusText>
                      </StatusBar>

                      {/* Overlapping Classes */}
                      {datasetInsights.warnings.filter((w: any) => w.type === 'overlap').length >
                        0 && (
                        <IssueGroup>
                          <IssueGroupTitle>
                            Overlapping Classes
                            {highlightedOverlaps.length > 0 && (
                              <ActionButton
                                variant="secondary"
                                onClick={() => {
                                  setHighlightedOverlaps([])
                                  setVisibleTags(new Set())
                                }}
                                style={{
                                  marginLeft: 'auto',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.7rem',
                                }}
                              >
                                Clear Filter
                              </ActionButton>
                            )}
                          </IssueGroupTitle>
                          <div
                            style={{
                              padding: '0 1rem 0.75rem 1rem',
                              fontSize: '0.75rem',
                              color: '#6c757d',
                              lineHeight: '1.4',
                            }}
                          >
                            <strong>Why This Happens:</strong> DINOv2 embeddings detected visual
                            similarity - even if objects are semantically different, they may share
                            colors, textures, shapes, or viewing angles that confuse the model.
                            <br />
                            <br />
                            <strong>What To Do:</strong>
                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                              <li>
                                <strong>Click a pair</strong> to view images side-by-side on the
                                chart
                              </li>
                              <li>
                                <strong>If mislabeled:</strong> Fix the labels before training
                              </li>
                              <li>
                                <strong>If labeled correctly:</strong> Add 15-30 more diverse
                                examples per class (different angles, lighting, colors, backgrounds)
                                - your model will learn to focus on the right distinguishing
                                features during training
                              </li>
                              <li>
                                <strong>If fundamentally too similar:</strong> Consider merging into
                                one class or using a different approach
                              </li>
                            </ul>
                            <div
                              style={{
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: '#e7f3ff',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                              }}
                            >
                              💡 <strong>Training will help!</strong> These embeddings are just for
                              visualization. Your actual model training will learn task-specific
                              features that better separate these classes.
                            </div>
                          </div>
                          <IssueList>
                            {datasetInsights.warnings
                              .filter((w: any) => w.type === 'overlap')
                              .flatMap((w: any) => w.overlappingPairs)
                              .filter((pair: any) => pair.distance < 200) // Only show High and Medium severity
                              .map((pair: any, idx: number) => {
                                const severity = getSeverityFromDistance(pair.distance)
                                return (
                                  <IssueItem
                                    key={idx}
                                    clickable
                                    onClick={() => handleShowPair(pair)}
                                  >
                                    <PairRow>
                                      <PairClasses>
                                        <ClassBadge color={getClassColor(pair.tag1)}>
                                          {pair.tag1}
                                        </ClassBadge>
                                        <span style={{ color: '#6c757d' }}>↔</span>
                                        <ClassBadge color={getClassColor(pair.tag2)}>
                                          {pair.tag2}
                                        </ClassBadge>
                                      </PairClasses>
                                      <PairDistance
                                        style={{ color: severity.color, fontWeight: 600 }}
                                      >
                                        {severity.label}
                                      </PairDistance>
                                    </PairRow>
                                    <div
                                      style={{
                                        fontSize: '0.7rem',
                                        color: '#6c757d',
                                        marginTop: '0.25rem',
                                      }}
                                    >
                                      Click to view these classes on chart
                                    </div>
                                  </IssueItem>
                                )
                              })}
                          </IssueList>
                        </IssueGroup>
                      )}

                      {/* Class Imbalance */}
                      {datasetInsights.warnings.filter((w: any) => w.type === 'imbalance').length >
                        0 && (
                        <IssueGroup>
                          <IssueGroupTitle>Class Imbalance</IssueGroupTitle>
                          <IssueList>
                            {datasetInsights.warnings
                              .filter((w: any) => w.type === 'imbalance')
                              .map((warning: any, idx: number) => (
                                <IssueItem key={idx}>
                                  <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                                    {warning.metrics?.mostCommonTag &&
                                      warning.metrics?.leastCommonTag && (
                                        <>
                                          <ClassBadge
                                            color={getClassColor(warning.metrics.mostCommonTag)}
                                          >
                                            {warning.metrics.mostCommonTag}:{' '}
                                            {warning.metrics.maxCount}
                                          </ClassBadge>
                                          {' vs '}
                                          <ClassBadge
                                            color={getClassColor(warning.metrics.leastCommonTag)}
                                          >
                                            {warning.metrics.leastCommonTag}:{' '}
                                            {warning.metrics.minCount}
                                          </ClassBadge>
                                        </>
                                      )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.75rem',
                                      color: '#adb5bd',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {warning.metrics?.ratio &&
                                      `${warning.metrics.ratio.toFixed(1)}:1 ratio`}
                                  </div>
                                  <ActionButton
                                    variant="secondary"
                                    onClick={() => setVisualizationMode('annotation')}
                                    style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                                  >
                                    View Distribution
                                  </ActionButton>
                                </IssueItem>
                              ))}
                          </IssueList>
                        </IssueGroup>
                      )}

                      {/* Other Issues */}
                      {datasetInsights.warnings.filter(
                        (w: any) => w.type !== 'overlap' && w.type !== 'imbalance'
                      ).length > 0 && (
                        <IssueGroup>
                          <IssueGroupTitle>Other Issues</IssueGroupTitle>
                          <IssueList>
                            {datasetInsights.warnings
                              .filter((w: any) => w.type !== 'overlap' && w.type !== 'imbalance')
                              .map((warning: any, idx: number) => (
                                <IssueItem key={idx}>
                                  <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
                                    {warning.message}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.75rem',
                                      color: '#adb5bd',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {warning.impact}
                                  </div>
                                  <ActionButton
                                    variant="secondary"
                                    onClick={() => {
                                      if (warning.type === 'outliers')
                                        setVisualizationMode('outliers')
                                      else setVisualizationMode('annotation')
                                    }}
                                    style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                                  >
                                    Review
                                  </ActionButton>
                                </IssueItem>
                              ))}
                          </IssueList>
                        </IssueGroup>
                      )}

                      {/* Success State */}
                      {datasetInsights.warnings.length === 0 && (
                        <IssueItem>
                          <div style={{ textAlign: 'center', padding: '1rem' }}>
                            <div
                              style={{
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#28a745',
                                marginBottom: '0.5rem',
                              }}
                            >
                              Dataset Ready
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>
                              No issues detected. You can proceed with training.
                            </div>
                          </div>
                        </IssueItem>
                      )}
                    </InsightsScrollContainer>
                  </InsightsPanel>
                )}
              </DashboardContainer>
            )}
          </Tab>
        </Tabs>
      </TabsContainer>

      {/* Content Viewer Modal */}
      <ContentViewerModal
        show={showContentViewer}
        onHide={() => {
          setShowContentViewer(false)
          // Update nodes/edges after modal closes, then restore viewport
          setTimeout(() => {
            setNodesState(zoomAdjustedNodes)
            setEdgesState(edges)
            // Restore viewport after nodes are updated
            if (reactFlowInstance && savedViewport) {
              setTimeout(() => {
                reactFlowInstance.setViewport(savedViewport)
              }, 50)
            }
          }, 50)
        }}
        size="xl"
        centered
      >
        <Modal.Header>
          <Modal.Title>
            <Eye size={20} className="me-2" />
            Selected Content ({selectedContentItems.length} items)
          </Modal.Title>
          <Button
            variant="link"
            onClick={() => {
              setShowContentViewer(false)
              // Update nodes/edges after modal closes, then restore viewport
              setTimeout(() => {
                setNodesState(zoomAdjustedNodes)
                setEdgesState(edges)
                // Restore viewport after nodes are updated
                if (reactFlowInstance && savedViewport) {
                  setTimeout(() => {
                    reactFlowInstance.setViewport(savedViewport)
                  }, 50)
                }
              }, 50)
            }}
            style={{ color: '#ffffff', textDecoration: 'none' }}
          >
            <X size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          {selectedContentItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#adb5bd' }}>
              No content selected. Use the lasso tool to select nodes first.
            </div>
          ) : (
            <ContentGrid>
              {selectedContentItems.map((item, index) => (
                <ContentItem key={item.id || index}>
                  {item.b64_image && (
                    <ContentImage
                      src={`data:${item.content_type || 'image/jpeg'};base64,${item.b64_image}`}
                      alt={item.name || `Content ${index + 1}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  )}
                  <ContentInfo>
                    <div className="content-name">{item.name || `Content ${index + 1}`}</div>
                    <div className="content-details">
                      {item.width && item.height && (
                        <div>
                          {item.width} × {item.height}
                        </div>
                      )}
                    </div>
                    <div className="annotation-status">
                      {item.annotation && item.annotation.length > 0 ? (
                        <div>
                          {item.annotation.map((ann: any, annIndex: number) => (
                            <div key={annIndex} style={{ marginBottom: '0.25rem' }}>
                              {renderTagBadges(ann.tagids)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                          }}
                        >
                          No tags
                        </span>
                      )}
                    </div>
                  </ContentInfo>
                </ContentItem>
              ))}
            </ContentGrid>
          )}
        </Modal.Body>
      </ContentViewerModal>

      {/* Help Modal */}
      <Modal
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        size="lg"
        centered
        style={{ zIndex: 9999 }}
      >
        <Modal.Header
          style={{
            backgroundColor: '#212529',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Modal.Title
            style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <HelpCircle size={24} />
            Understanding the Displacement Chart
          </Modal.Title>
          <Button
            variant="link"
            onClick={() => setShowHelpModal(false)}
            style={{ color: '#ffffff', textDecoration: 'none' }}
          >
            <X size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: '#212529',
            color: '#ffffff',
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: '1rem' }}>
            {/* What is this? */}
            <div style={{ marginBottom: '2rem' }}>
              <h5
                style={{
                  color: '#667eea',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Activity size={20} />
                What is the Displacement Chart?
              </h5>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                The Displacement Chart visualizes your dataset in 2D space using{' '}
                <strong>PCA (Principal Component Analysis)</strong>. It shows how similar or
                different your images are based on their AI-generated embeddings.
              </p>
              <p style={{ lineHeight: '1.6', marginBottom: '0' }}>
                <strong>Key insight:</strong> Images that are{' '}
                <span style={{ color: '#28a745' }}>close together</span> are visually similar, while
                images that are <span style={{ color: '#dc3545' }}>far apart</span> are different.
              </p>
            </div>

            {/* How to read it */}
            <div style={{ marginBottom: '2rem' }}>
              <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>How to Read the Chart</h5>
              <ul style={{ lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>
                  <strong>Each dot</strong> = one image in your dataset
                </li>
                <li>
                  <strong>Position</strong> = similarity to other images (closer = more similar)
                </li>
                <li>
                  <strong>Color</strong> = tag category, source, size, or annotation status
                </li>
                <li>
                  <strong>Clusters</strong> = groups of similar images (badges show count)
                </li>
              </ul>
            </div>

            {/* Visualization Modes */}
            <div style={{ marginBottom: '2rem' }}>
              <h5
                style={{
                  color: '#667eea',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Palette size={20} />
                Visualization Modes
              </h5>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                  }}
                >
                  <strong style={{ color: '#fbbf24' }}>By Tag (Default)</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Colors images by their most common annotation tag. Great for finding patterns in
                    labeled data.
                  </p>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                  }}
                >
                  <strong style={{ color: '#fbbf24' }}>By Source</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Colors by data source. Useful for comparing different datasets or folders.
                  </p>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                  }}
                >
                  <strong style={{ color: '#fbbf24' }}>By Size</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Colors by image dimensions. Find outliers with unusual sizes.
                  </p>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                  }}
                >
                  <strong style={{ color: '#fbbf24' }}>Outliers</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Highlights unusual images based on position, size, and annotation patterns.
                  </p>
                </div>
              </div>
            </div>

            {/* Clustering */}
            <div style={{ marginBottom: '2rem' }}>
              <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>Clustering</h5>
              <p style={{ lineHeight: '1.6', marginBottom: '1rem' }}>
                Similar images are automatically grouped into <strong>clusters</strong> (shown as
                numbered badges). This makes it easier to work with large datasets.
              </p>
              <ul style={{ lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>
                  <strong>Click a cluster</strong> to expand and see individual images
                </li>
                <li>
                  <strong>Numbers show</strong> how many images are in each group
                </li>
                <li>
                  <strong>Same color</strong> = similar images with same tag/category
                </li>
              </ul>
            </div>

            {/* Interactive Features */}
            <div style={{ marginBottom: '2rem' }}>
              <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>Interactive Features</h5>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '0.5rem',
                    borderLeft: '3px solid #667eea',
                  }}
                >
                  <strong>Lasso Select</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Draw around images to select multiple at once. View selected images in a grid.
                  </p>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '0.5rem',
                    borderLeft: '3px solid #667eea',
                  }}
                >
                  <strong>Legend Filtering</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Click tags in the legend to show/hide specific categories.
                  </p>
                </div>
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '0.5rem',
                    borderLeft: '3px solid #667eea',
                  }}
                >
                  <strong>Pan & Zoom</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Drag to pan, scroll to zoom. Use "Fit View" button to reset.
                  </p>
                </div>
              </div>
            </div>

            {/* Common Use Cases */}
            <div style={{ marginBottom: '2rem' }}>
              <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>Common Use Cases</h5>
              <ul style={{ lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                <li>
                  <strong>Find similar images:</strong> Look for tight clusters
                </li>
                <li>
                  <strong>Discover outliers:</strong> Find isolated dots far from others
                </li>
                <li>
                  <strong>Validate labels:</strong> Check if same-tag images cluster together
                </li>
                <li>
                  <strong>Explore dataset:</strong> See distribution and diversity at a glance
                </li>
                <li>
                  <strong>Quality control:</strong> Find unusual or mislabeled images
                </li>
              </ul>
            </div>

            {/* Training-Specific Guidance */}
            <div
              style={{
                marginBottom: '2rem',
                padding: '1rem',
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(102, 126, 234, 0.3)',
              }}
            >
              <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>
                🎯 How This Helps AI Model Training
              </h5>

              <div style={{ marginBottom: '1rem' }}>
                <h6 style={{ color: '#fbbf24', marginBottom: '0.5rem' }}>What to Look For:</h6>
                <ul style={{ lineHeight: '1.6', paddingLeft: '1.5rem', fontSize: '0.9rem' }}>
                  <li>
                    <strong>Well-Separated Clusters:</strong> Different tags should form distinct
                    groups. If they overlap heavily, your model will struggle to tell them apart.
                  </li>
                  <li>
                    <strong>Class Balance:</strong> Roughly equal-sized clusters per tag. Large
                    imbalances mean your model will be biased toward the larger class.
                  </li>
                  <li>
                    <strong>Outliers:</strong> Isolated dots may be mislabeled or corrupted - review
                    them before training.
                  </li>
                  <li>
                    <strong>Within-Class Tightness:</strong> Images with the same tag should cluster
                    together. If scattered, your labels may be inconsistent.
                  </li>
                </ul>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <h6 style={{ color: '#fbbf24', marginBottom: '0.5rem' }}>
                  What Means "Ready to Train":
                </h6>
                <ul style={{ lineHeight: '1.6', paddingLeft: '1.5rem', fontSize: '0.9rem' }}>
                  <li>✅ Clear separation between different tag clusters</li>
                  <li>✅ Balanced number of images per class (within 3:1 ratio)</li>
                  <li>✅ Few or no outliers (&lt;5%)</li>
                  <li>✅ At least 100+ images total, 50+ per class minimum</li>
                  <li>✅ Same-tag images grouped together</li>
                </ul>
              </div>

              <div>
                <h6 style={{ color: '#dc3545', marginBottom: '0.5rem' }}>Red Flags:</h6>
                <ul style={{ lineHeight: '1.6', paddingLeft: '1.5rem', fontSize: '0.9rem' }}>
                  <li>🚫 Different tags heavily overlapping → Model won't learn to distinguish</li>
                  <li>🚫 One class 10x+ larger than others → Biased predictions</li>
                  <li>🚫 Same-tag images scattered everywhere → Inconsistent labeling</li>
                  <li>🚫 Many isolated outliers → Data quality issues</li>
                  <li>🚫 Very small dataset (&lt;100 images) → High overfitting risk</li>
                </ul>
              </div>
            </div>

            {/* Automated Insights */}
            <div
              style={{
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(40, 167, 69, 0.3)',
              }}
            >
              <h5 style={{ color: '#28a745', marginBottom: '0.75rem' }}>🤖 Automated Insights</h5>
              <p style={{ margin: '0', fontSize: '0.9rem', lineHeight: '1.6' }}>
                The <strong>"Training Insights"</strong> panel (bottom-left) automatically analyzes
                your dataset and provides:
              </p>
              <ul
                style={{
                  lineHeight: '1.6',
                  paddingLeft: '1.5rem',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem',
                }}
              >
                <li>
                  <strong>Class imbalance warnings</strong> with recommended ratios
                </li>
                <li>
                  <strong>Class separation analysis</strong> to predict model accuracy
                </li>
                <li>
                  <strong>Outlier detection</strong> to find suspicious data
                </li>
                <li>
                  <strong>Dataset size recommendations</strong> to avoid overfitting
                </li>
                <li>
                  <strong>Actionable fixes</strong> for every issue detected
                </li>
              </ul>
            </div>

            <div
              style={{
                padding: '1rem',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(251, 191, 36, 0.3)',
              }}
            >
              <strong style={{ color: '#fbbf24' }}>💡 Pro Workflow:</strong>
              <ol
                style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  paddingLeft: '1.5rem',
                }}
              >
                <li>
                  Check the <strong>Training Insights</strong> panel for automated warnings
                </li>
                <li>
                  Use <strong>"By Tag"</strong> mode to verify class separation
                </li>
                <li>
                  Switch to <strong>"Outliers"</strong> mode to review suspicious images
                </li>
                <li>
                  Use <strong>Lasso Select</strong> to review overlapping clusters
                </li>
                <li>Fix issues (relabel, balance, remove outliers)</li>
                <li>Regenerate PCA to see improvements</li>
                <li>Train when insights show green "ready" status!</li>
              </ol>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: '#212529', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <Button variant="primary" onClick={() => setShowHelpModal(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </ChartContainer>
  )
}

export default DisplacementChart
